import { Fragment, useCallback, useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { api } from '../../api/client';
import { fmtLocalDate, fmtLocalDateTime } from '../../utils/datetime';   // §14b.6 — never format dates inline
import { useConfirm } from '../../components/confirm';
import { serverMessage } from '../../components/confirm/serverMessage';
import { AppDataGrid, type AppColumn } from '../../components/AppDataGrid';
import { AppDialog } from '../../components/AppDialog';
import type { NotifyApi } from '../../components/notifications';
import { AiCombo } from '../../components/AiCombo';
import { CountryDetailModal } from './CountryDetailModal';
import { StateDetailModal } from './StateDetailModal';
import { CityDetailModal } from './CityDetailModal';
import { ContactDetailModal } from './ContactDetailModal';
import { ModuleCrudDialog } from '../modules/ModuleCrudDialog';
import { MODULE_CRUD } from '../modules/moduleCrud';
import { ProjectDetailModal } from './ProjectDetailModal';
import { EXPORT_FORMATS, exportRows, type ExportFormat } from './gridExport';
import { useModalKeys } from '../../hooks/useModalKeys';
import { MaintenanceDetailFrame } from '../maintenance/MaintenanceDetailFrame';
import { SpinInput } from '../../components/SpinInput';
import type { MaintenanceDetailWiring } from '../maintenance/MaintenanceDetailFrame';
import { SrCard, SrLayout, SrSingleEdit, useSrLayout, useSrSingleEdit } from '../maintenance/srLayout';

// ─────────────────────────────────────────────────────────────────────────────
// GoLive "{Client} - Clients" detail popup: Summary / Address / Account
// Management / Sending Preference fields + Contacts / Invoicing Contact /
// Projects / Collectors / Sample Sites / Notes tabs, each with its own
// New/Edit/Delete/Export grid. Opened from the Client picker's row Edit icon
// (Sample Receipt Notification → New Contact → Client picker).
// ─────────────────────────────────────────────────────────────────────────────

interface ClientDetail {
  oid: string; customerName: string; clientNumber: string | null; clientCode: string | null;
  officePhone: string | null; webSite: string | null;
  accountId: string | null; accountDisplay: string | null;
  industryId: string | null; industryDisplay: string | null;
  categoryId: string | null; categoryDisplay: string | null;
  fax: string | null; primaryContactId: string | null;
  primaryContactName: string | null; email: string | null; licenseNumber: string | null;
  customerId: string | null; retiredDate: string | null;
  address: string | null; address1: string | null; postCode: string | null;
  countryId: string | null; countryName: string | null; stateId: string | null; stateName: string | null;
  cityId: string | null; cityName: string | null; zip: string | null; sicCode: string | null;
  ownerId: string | null; ownerName: string | null;
  sourceLeadId: string | null; sourceLeadDisplay: string | null;
  creditLimit: number | null; preferredContactMethod: number;
  contractDate: string | null; startDate: string | null;
  manintenanceStartDate: string | null; manintenanceEndDate: string | null;
  acceptanceDate: string | null; reminderDate: string | null;
  mail: boolean; sendEmail: boolean;
}

export interface Opt { id: string; name: string; parentId?: string | null; }

// GoLive-style compact combobox: replaces the native <select> whose popup fills
// the whole screen for long lists (e.g. Owner = every Employee). Renders a
// trigger + a portal popup capped at ~240px with its own scroll, so it shows a
// short scrollable list exactly like the DevExpress combo (req 4).
export function Combo({ value, options, onChange, disabled }: {
  value: string | null; options: Opt[]; onChange: (v: string | null) => void; disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  // FK ids compare case-INSENSITIVELY: lookup option ids come from SQL
  // CAST(uniqueidentifier AS NVARCHAR) (UPPERCASE) while the detail's own ids /
  // the created-row oid are C#/JSON GUIDs (lowercase) — a case-sensitive match
  // would never preselect the current value or a just-created row.
  const eq = (a: string | null, b: string | null) => (a ?? '').toLowerCase() === (b ?? '').toLowerCase();
  const selected = options.find(o => eq(o.id, value));

  const place = () => {
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    setPos({ top: Math.min(r.bottom + 2, window.innerHeight - 60), left: r.left, width: r.width });
  };
  useLayoutEffect(() => { if (open) place(); }, [open]);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (popRef.current?.contains(t) || btnRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    const reanchor = (e: Event) => { if (popRef.current?.contains(e.target as Node)) return; place(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    document.addEventListener('scroll', reanchor, true);
    window.addEventListener('resize', reanchor);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('scroll', reanchor, true);
      window.removeEventListener('resize', reanchor);
    };
  }, [open]);

  const row = (id: string | null, name: string): ReactNode => {
    const on = eq(id, value);
    return (
      <div key={id ?? '__na'} onClick={() => { onChange(id); setOpen(false); }}
           style={{ padding: '4px 9px', cursor: 'pointer', whiteSpace: 'nowrap',
                    background: on ? '#fff3ea' : '#fff', color: on ? 'var(--brand-accent,#e8670a)' : 'inherit' }}
           onMouseEnter={e => { e.currentTarget.style.background = on ? '#fff3ea' : '#f1f3f7'; }}
           onMouseLeave={e => { e.currentTarget.style.background = on ? '#fff3ea' : '#fff'; }}>
        {name}
      </div>
    );
  };

  return (
    <>
      {/* Disabled = the parent lookup isn't set yet (GoLive
          DataSourcePropertyIsNullMode.SelectNothing — e.g. State before a Country
          is chosen): greyed, not clickable, no dropdown popup. */}
      <button type="button" ref={btnRef} disabled={disabled} onClick={() => !disabled && setOpen(o => !o)}
              title={disabled ? 'Select the parent value first' : undefined}
              style={{ ...fInput(false), textAlign: 'left', cursor: disabled ? 'not-allowed' : 'pointer',
                       background: disabled ? '#f3f4f6' : '#fff', color: disabled ? '#9aa1ad' : 'inherit',
                       display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selected?.name || 'N/A'}</span>
        <span style={{ color: disabled ? '#c3c9d2' : '#8a9099', fontSize: 9 }}><i className="ti ti-chevron-down" /></span>
      </button>
      {open && pos && createPortal(
        <div ref={popRef} role="listbox"
             style={{ position: 'fixed', top: pos.top, left: pos.left, width: pos.width, maxHeight: 240,
                      overflowY: 'auto', background: '#fff', border: '1px solid #cbd5e1', borderRadius: 5,
                      boxShadow: '0 6px 20px rgba(0,0,0,0.18)', zIndex: 2000, fontSize: 12 }}>
          {row(null, 'N/A')}
          {options.map(o => row(o.id, o.name))}
        </div>, document.body)}
    </>
  );
}

/** Maintenance page only: what the client page remembers across its own Refresh (the editor is remounted on Refresh) — the open tab and
 *  the ticked rows of each tab grid. Cleared when the record changes or the page is closed; the popup versions never use it. */
const maintTabMemory = { clientId: '', tab: undefined as TabKey | undefined, ticks: new Map<string, Set<string>>() };
export function clearMaintTabMemory() { maintTabMemory.clientId = ''; maintTabMemory.tab = undefined; maintTabMemory.ticks.clear(); }

type TabKey = 'relinquished' | 'contacts' | 'invoicing' | 'projects' | 'collectors' | 'sites' | 'notes' | 'accounts';
const TABS: { key: TabKey; label: string }[] = [
  // G5's client page lists Relinquished By first (Customer.BroughtByList), then the rest.
  { key: 'relinquished', label: 'Relinquished By' },
  { key: 'contacts',   label: 'Contacts' },
  { key: 'invoicing',  label: 'Invoicing Contact' },
  { key: 'projects',   label: 'Projects' },
  { key: 'collectors', label: 'Collectors' },
  { key: 'sites',      label: 'Sample Sites' },
  { key: 'notes',      label: 'Notes' },
  // G5 Customer.AccountNumber — last tab; Maintenance page only.
  { key: 'accounts',   label: 'Accounts' },
];

const PREFERRED_CONTACT_METHODS = ['Any', 'Email', 'Phone', 'Fax', 'Mail'];

/** A blank client for New — every field empty, Preferred Contact Method "Any". */
const BLANK_CLIENT: ClientDetail = {
  oid: '', customerName: '', clientNumber: null, clientCode: null, officePhone: null, webSite: null,
  accountId: null, accountDisplay: null, industryId: null, industryDisplay: null,
  categoryId: null, categoryDisplay: null, fax: null, primaryContactId: null,
  primaryContactName: null, email: null, licenseNumber: null, customerId: null, retiredDate: null,
  address: null, address1: null, postCode: null, countryId: null, countryName: null, stateId: null,
  stateName: null, cityId: null, cityName: null, zip: null, sicCode: null, ownerId: null, ownerName: null,
  sourceLeadId: null, sourceLeadDisplay: null, creditLimit: null, preferredContactMethod: 0,
  contractDate: null, startDate: null, manintenanceStartDate: null, manintenanceEndDate: null,
  acceptanceDate: null, reminderDate: null, mail: false, sendEmail: false,
};

/** Page-mode wiring supplied by the Maintenance list (breadcrumb, prev / next, New · Delete · Refresh · Audit). */
export type ClientDetailPage = MaintenanceDetailWiring;
/**
 * The full Client form (GoLive Customer_DetailView_SampleRegistration).
 *
 * `clientId` null = NEW (added 2026-10-20 for Open Quote ▸ Client ▸ New, which
 * opens this same form in legacy). The first Save creates the client
 * (POST /api/v1/clients) and writes the rest of the form to it, after which the
 * modal carries on as an edit of the new client so its tabs (Contacts,
 * Projects, …) can be filled in — they need the client to exist. `onSaved`
 * receives the client's id. Every existing caller passes an id and is unchanged.
 */
export function ClientDetailModal({ clientId: clientIdProp, onClose, onSaved, notify, zIndex = 1300, page }: {
  clientId: string | null; onClose: () => void; onSaved: (clientId?: string) => void; notify: NotifyApi;
  /** When set the editor renders as the full-page Detail View (Maintenance ▸ Clients) instead of a popup. */
  page?: ClientDetailPage;
  /** Base overlay z-index — bumped when this modal is opened NESTED (e.g. from
   *  the Project popup's Client ✎) so it sits above the opener. Default 1300. */
  zIndex?: number;
}) {
  const { confirm } = useConfirm();
  // The client being edited; '' until a NEW client is first saved.
  const [clientId, setClientId] = useState(clientIdProp ?? '');
  const isNew = clientId === '';
  // Bespoke popup keyboard (standing rule 14): Esc closes this hand-rolled overlay.
  const modalKeys = useModalKeys(true, { onClose });
  const [max, setMax] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState<ClientDetail | null>(null);
  const [form, setForm] = useState<ClientDetail | null>(null);
  // Relinquished By is G5's FIRST client tab, shown on the Maintenance page only — the Sample Registration / Quotes popup keeps its own tabs.
  const [tab, setTab] = useState<TabKey>(() => {
    if (!page) return 'contacts';
    if (clientIdProp && maintTabMemory.clientId !== clientIdProp) { clearMaintTabMemory(); maintTabMemory.clientId = clientIdProp; }
    return maintTabMemory.tab ?? 'relinquished';
  });
  // Combobox lookups (GoLive FK fields) + cascading geo.
  const [owners, setOwners]         = useState<Opt[]>([]);
  const [categories, setCategories] = useState<Opt[]>([]);
  const [industries, setIndustries] = useState<Opt[]>([]);
  const [sourceLeads, setSourceLeads] = useState<Opt[]>([]);
  const [, setAccounts]            = useState<Opt[]>([]);   // read only by the Account-number popup flow; the form itself has no Account field
  const [contactOpts, setContactOpts] = useState<Opt[]>([]);
  const [countries, setCountries]   = useState<Opt[]>([]);
  // G5 Customer.AfterConstruction: a new client starts in the United States.
  useEffect(() => {
    if (clientId !== '' || !countries.length) return;
    const us = countries.find(c => /^(us|usa|united states( of america)?)$/i.test(c.name.trim()));
    if (us) setForm(p => (p && !p.countryId ? { ...p, countryId: us.id } : p));
  }, [clientId, countries, form === null]);   // eslint-disable-line react-hooks/exhaustive-deps
  const [states, setStates]         = useState<Opt[]>([]);
  const [cities, setCities]         = useState<Opt[]>([]);
  // Geo lookups carry a mode: 'new' opens a blank create form, 'edit' opens the
  // selected record (GoLive New vs Edit LookupPropertyEditor buttons).
  const [countryModal, setCountryModal] = useState<false | 'new' | 'edit'>(false);
  const [accountModalOpen, setAccountModalOpen] = useState(false);
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [stateModal, setStateModal] = useState<false | 'new' | 'edit'>(false);
  const [cityModal, setCityModal] = useState<false | 'new' | 'edit'>(false);
  const [sourceLeadModalOpen, setSourceLeadModalOpen] = useState(false);

  const cid = encodeURIComponent(clientId);
  const reloadAccounts = useCallback(async () => {
    try { const r = await api.get(`/api/v1/clients/${cid}/accounts`); setAccounts((r.data as { data?: Opt[] }).data ?? []); } catch { /* keep */ }
  }, [cid]);
  const reloadCategories = useCallback(async () => {
    try { const r = await api.get('/api/v1/clients/lookups/categories'); setCategories((r.data as { data?: Opt[] }).data ?? []); } catch { /* keep */ }
  }, []);
  const reloadContactOpts = useCallback(async () => {
    try { const r = await api.get(`/api/v1/clients/${cid}/contact-options`); setContactOpts((r.data as { data?: Opt[] }).data ?? []); } catch { /* keep */ }
  }, [cid]);
  const reloadSourceLeads = useCallback(async () => {
    try { const r = await api.get('/api/v1/clients/lookups/source-leads'); setSourceLeads((r.data as { data?: Opt[] }).data ?? []); } catch { /* keep */ }
  }, []);

  const loadGeo = useCallback(async () => {
    try {
      const [cs, st, ci] = await Promise.all([
        api.get('/api/v1/system/states/countries'),
        api.get('/api/v1/cities/states'),
        api.get('/api/v1/cities'),
      ]);
      setCountries(((cs.data as { data?: { id: string; name: string }[] }).data ?? []).map(c => ({ id: c.id, name: c.name, parentId: null })));
      setStates(((st.data as { data?: { id: string; shortName: string; longName: string | null; countryId: string | null }[] }).data ?? []).map(s => ({ id: s.id, name: s.longName || s.shortName, parentId: s.countryId })));
      setCities(((ci.data as { data?: { id: string; cityName: string | null; stateId: string | null }[] }).data ?? []).map(c => ({ id: c.id, name: c.cityName ?? '', parentId: c.stateId })));
    } catch { /* geo optional */ }
  }, []);

  useEffect(() => {
    // Each lookup is independent — a slow/failed one (e.g. DB timeout on source-leads)
    // must not blank the others or the detail. Load + set each on its own.
    const fill = (url: string, setter: (v: Opt[]) => void) =>
      api.get(url).then(r => setter((r.data as { data?: Opt[] }).data ?? [])).catch(() => { /* leave empty */ });

    void loadGeo();
    void fill('/api/v1/clients/lookups/owners', setOwners);
    void fill('/api/v1/clients/lookups/categories', setCategories);
    void fill('/api/v1/clients/lookups/industries', setIndustries);
    void fill('/api/v1/clients/lookups/source-leads', setSourceLeads);
    // New: nothing to read yet — and the per-client lists need a client.
    if (!clientId) { setDetail(null); setForm({ ...BLANK_CLIENT }); setLoading(false); return; }

    void (async () => {
      setLoading(true);
      try {
        const d = await api.get(`/api/v1/clients/${cid}`);
        setDetail((d.data as { data: ClientDetail }).data);
        setForm((d.data as { data: ClientDetail }).data);
      } catch { notify.error('Failed to load client details.'); }
      finally { setLoading(false); }
    })();

    void fill(`/api/v1/clients/${cid}/accounts`, setAccounts);
    void fill(`/api/v1/clients/${cid}/contact-options`, setContactOpts);
  }, [clientId, notify, loadGeo]);

  // The write itself — throws on failure. Shared by the popup's OK and the page's Save set, so a client is stored one way.
  const persist = useCallback(async (): Promise<string> => {
    if (!form) throw new Error('No client to save.');
    // New: create the client first (the server assigns its ClientCode), then
    // write the whole form to it with the same PUT an edit uses.
    let id = clientId;
    if (!id) {
      const created = await api.post('/api/v1/clients', {
        customerName: form.customerName, officePhone: form.officePhone, email: form.email,
      });
      id = String((created.data as { data: { oid: string } }).data.oid);
    }
    await api.put(`/api/v1/clients/${encodeURIComponent(id)}`, {
      customerName: form.customerName, clientNumber: form.clientNumber, clientCode: form.clientCode,
      officePhone: form.officePhone, webSite: form.webSite, fax: form.fax,
      // No accountId: G5's client form has no Account field, so the stored one is left alone.
      categoryId: form.categoryId, industryId: form.industryId,
      primaryContactId: form.primaryContactId,
      email: form.email, licenseNumber: form.licenseNumber, customerId: form.customerId,
      retiredDate: form.retiredDate, address: form.address, address1: form.address1, postCode: form.postCode,
      countryId: form.countryId, stateId: form.stateId, cityId: form.cityId, zip: form.zip, sicCode: form.sicCode,
      ownerId: form.ownerId, sourceLeadId: form.sourceLeadId,
      creditLimit: form.creditLimit, preferredContactMethod: form.preferredContactMethod,
      contractDate: form.contractDate, startDate: form.startDate,
      manintenanceStartDate: form.manintenanceStartDate, manintenanceEndDate: form.manintenanceEndDate,
      acceptanceDate: form.acceptanceDate, reminderDate: form.reminderDate,
      mail: form.mail, sendEmail: form.sendEmail,
    });
    if (!clientId) setClientId(id);   // carry on as an edit of the new client
    return id;
  }, [form, clientId]);

  const save = useCallback(async () => {
    if (!form) return;
    const ok = await confirm({ title: 'Save', message: 'Do you want to Save?' });
    if (!ok) return;
    if (!form.customerName?.trim()) { notify.warning("'Client' must not be empty."); return; }
    setSaving(true);
    try {
      const id = await persist();
      notify.success('Saved successfully.');
      onSaved(id);
    } catch (e) { notify.error(serverMessage(e) ?? 'Failed to save client.'); }
    finally { setSaving(false); }
  }, [form, confirm, notify, onSaved, persist]);

  const set = <K extends keyof ClientDetail>(key: K, value: ClientDetail[K]) =>
    setForm(p => p ? { ...p, [key]: value } : p);

  // GoLive FK combobox: compact scrollable dropdown (N/A + options) + optional
  // edit (✎) + eraser (✕).
  // GoLive lookup button order: dropdown · EDIT (New/Edit) · REMOVE (eraser).
  // Corrected 2026-08-01 — AI had the last two swapped (remove before edit).
  // `disabled` mirrors GoLive's DataSourcePropertyIsNullMode.SelectNothing — the
  // combo is greyed until its PARENT lookup has a value (State needs Country,
  // City needs State: Customer.cs `[DataSourceProperty("Country.States", …)]` /
  // `[DataSourceProperty("State.Cities", …)]`). The New/Edit (✎) button follows
  // the SAME dependency and is HIDDEN while gated — GoLive reveals the editors one
  // level at a time (Country always, State once a Country is picked, City once a
  // State is picked), since a state can't be authored without its country nor a
  // city without its state. The eraser stays throughout so a stale value inherited
  // from the record can always be cleared.
  // Lookup editor = combo + the GoLive XAF LookupPropertyEditor "New" button:
  //  • New (ti-plus)   — opens a BLANK create form for the related object. GoLive
  //                      shows "New" (NOT "Edit") on these lookups: even when a
  //                      value is already selected the button opens blank, never
  //                      pre-filled. Editing an existing Country/State/City/Account
  //                      is done in its own Settings ▸ System Management form.
  //  • Remove (eraser) — clears the value to N/A.
  // `onNew` opens the create popup; lookups with no create editor (Industry,
  // Owner, Primary Contact) pass none and show just the combo + eraser.
  const lookup = (value: string | null, options: Opt[], onChange: (v: string | null) => void, onNew?: () => void, disabled = false): ReactNode => (
    <AiCombo value={value} options={options.map(o => ({ value: o.id, label: o.name }))}
             onChange={onChange} onNew={disabled ? undefined : onNew} newTitle="New" disabled={disabled} erasable />
  );

  // GoLive shows date fields BLANK (calendar dropdown only) — hide the native
  // "mm/dd/yyyy" placeholder while empty (same trick as the New Contact Birthday
  // and the accounting date inputs): the `cdm-date-empty` class makes the
  // datetime-edit text transparent until the field has a value or is focused.
  const dateInput = (value: string | null, onChange: (v: string | null) => void): ReactNode => (
    <input type="date" className={`cdm-date${value ? '' : ' cdm-date-empty'}`} style={fInput(false)}
           value={value?.slice(0, 10) ?? ''} onChange={e => onChange(e.target.value || null)} />
  );

  const bodyEl = loading || !form ? (
      <div style={{ flex: 1, minHeight: 160, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6b7280' }}>Loading…</div>
    ) : (
      <div style={page ? undefined : { flex: 1, overflow: 'auto', padding: 16 }}>
        <style>{'.cdm-date.cdm-date-empty:not(:focus)::-webkit-datetime-edit{color:transparent}'}</style>
        {/* SUMMARY — GoLive exact 2-column order (grid fills L,R row by row). */}
        <Section sr={!!page} title="Summary" legacyTitle="SUMMARY" icon="📋">
          {page ? (
            <>
              {/* Maintenance page = G5's Summary exactly: no Account field (G5 keeps it on the Accounts tab), pairs
                  Category·WebSite, Fax·Industry, Email·Primary Contact, Customer ID·License Number, then Retired Date alone. */}
            <Fragment key="g5-0"><Field label="Client" required><input style={fInput(!form.customerName)} value={form.customerName} onChange={e => set('customerName', e.target.value)} /></Field></Fragment>
            <Fragment key="g5-1"><Field label="Client Number"><input style={fInput(false)} value={form.clientNumber ?? ''} onChange={e => set('clientNumber', e.target.value)} /></Field></Fragment>
            <Fragment key="g5-2"><Field label="Client Code"><input style={fReadOnly} value={form.clientCode ?? ''} placeholder={isNew ? 'Autogenerate' : undefined} readOnly /></Field></Fragment>
            <Fragment key="g5-3"><Field label="Phone"><input style={fInput(false)} value={form.officePhone ?? ''} onChange={e => set('officePhone', e.target.value)} /></Field></Fragment>
            <Fragment key="g5-4"><Field label="Category">{lookup(form.categoryId, categories, v => set('categoryId', v), () => setCategoryModalOpen(true))}</Field></Fragment>
            <Fragment key="g5-5"><Field label="WebSite"><input style={fInput(false)} value={form.webSite ?? ''} onChange={e => set('webSite', e.target.value)} /></Field></Fragment>
            <Fragment key="g5-6"><Field label="Fax"><input style={fInput(false)} value={form.fax ?? ''} onChange={e => set('fax', e.target.value)} /></Field></Fragment>
            <Fragment key="g5-7"><Field label="Industry">{lookup(form.industryId, industries, v => set('industryId', v))}</Field></Fragment>
            <Fragment key="g5-8"><Field label="Email"><input style={fInput(false)} value={form.email ?? ''} onChange={e => set('email', e.target.value)} /></Field></Fragment>
            <Fragment key="g5-9"><Field label="Primary Contact">{lookup(form.primaryContactId, contactOpts, v => set('primaryContactId', v))}</Field></Fragment>
            <Fragment key="g5-10"><Field label="Customer ID"><input style={fInput(false)} value={form.customerId ?? ''} onChange={e => set('customerId', e.target.value)} /></Field></Fragment>
            <Fragment key="g5-11"><Field label="License Number"><input style={fInput(false)} value={form.licenseNumber ?? ''} onChange={e => set('licenseNumber', e.target.value)} /></Field></Fragment>
            <Fragment key="g5-gap"><div /><div /></Fragment>
            <Fragment key="g5-ret"><Field label="Retired Date">{dateInput(form.retiredDate, v => set('retiredDate', v))}</Field></Fragment>
            </>
          ) : (
            <>
            <Field label="Client" required><input style={fInput(!form.customerName)} value={form.customerName} onChange={e => set('customerName', e.target.value)} /></Field>
            <Field label="Client Number"><input style={fInput(false)} value={form.clientNumber ?? ''} onChange={e => set('clientNumber', e.target.value)} /></Field>
            {/* Client Code is SYSTEM-ASSIGNED, never typed: legacy Customer.cs
                OnSaving mints it as MAX(ClientCode)+1 formatted "D4" (0019, …)
                when empty, so GoLive shows it greyed/read-only. It still round-trips
                on save so the existing value is preserved. */}
            <Field label="Client Code"><input style={fReadOnly} value={form.clientCode ?? ''} placeholder={isNew ? 'Autogenerate' : undefined} readOnly /></Field>
            <Field label="Phone"><input style={fInput(false)} value={form.officePhone ?? ''} onChange={e => set('officePhone', e.target.value)} /></Field>
            {/* No Account field: G5's Client detail does not have one (its editor is hidden, Index -1, and its layout item sits in a
                Removed group). A client's account numbers are managed on the Accounts tab.
                The fields run in ROW order of the two columns so they land as G5 has them — left: Client · Client Code · Category ·
                Fax · Email; right: Client Number · Phone · WebSite · Industry · Primary Contact · License Number. */}
            <Field label="Category">{lookup(form.categoryId, categories, v => set('categoryId', v), () => setCategoryModalOpen(true))}</Field>
            {/* WebSite is a plain editable string on the legacy BO (Customer.cs
                #region WebSite) — it was hard-wired read-only/blank here. Now bound
                to dbo.Customer.WebSite through the detail read + update. */}
            <Field label="WebSite"><input style={fInput(false)} value={form.webSite ?? ''} onChange={e => set('webSite', e.target.value)} /></Field>
            <Field label="Fax"><input style={fInput(false)} value={form.fax ?? ''} onChange={e => set('fax', e.target.value)} /></Field>
            <Field label="Industry">{lookup(form.industryId, industries, v => set('industryId', v))}</Field>
            <Field label="Email"><input style={fInput(false)} value={form.email ?? ''} onChange={e => set('email', e.target.value)} /></Field>
            {/* Primary Contact — GoLive Customer.PrimaryContact is a PLAIN combo
                over ContactsDataSource (this client's own Contacts); it has no
                New/Edit editor, so no ✎ here. New contacts are added on the
                Contacts tab below, which refreshes this list. */}
            <Field label="Primary Contact">{lookup(form.primaryContactId, contactOpts, v => set('primaryContactId', v))}</Field>
            <div />
            <Field label="License Number"><input style={fInput(false)} value={form.licenseNumber ?? ''} onChange={e => set('licenseNumber', e.target.value)} /></Field>
            {/* The G5 Client popup has no Customer ID or Retired Date (the full-page Maintenance form does) — values are kept, just not shown. */}
            </>
          )}
        </Section>

        {/* ADDRESS — GoLive exact order. */}
        <Section sr={!!page} title="Address" legacyTitle="ADDRESS" icon="📍">
          <Field label="Address 1"><input style={fInput(false)} value={form.address ?? ''} onChange={e => set('address', e.target.value)} /></Field>
          <Field label="Address 2"><input style={fInput(false)} value={form.address1 ?? ''} onChange={e => set('address1', e.target.value)} /></Field>
          <Field label="Country">{lookup(form.countryId, countries, v => setForm(p => p ? { ...p, countryId: v, stateId: null, cityId: null } : p), () => setCountryModal('new'))}</Field>
          <Field label="Post Code"><input style={fInput(false)} value={form.postCode ?? ''} onChange={e => set('postCode', e.target.value)} /></Field>
          {/* State — GoLive Customer.State: data source is Country.States and
              SelectNothing while Country is null, so the combo is greyed until
              a Country is picked and then lists ONLY that Country's states. */}
          <Field label="State">{lookup(form.stateId, states.filter(s => s.parentId === form.countryId), v => setForm(p => p ? { ...p, stateId: v, cityId: null } : p), () => setStateModal('new'), !form.countryId)}</Field>
          <Field label="Zip"><input style={fInput(false)} value={form.zip ?? ''} onChange={e => set('zip', e.target.value)} /></Field>
          {/* City — GoLive Customer.City: data source is State.Cities, greyed
              until a State is picked (which itself needs a Country). */}
          <Field label="City">{lookup(form.cityId, cities.filter(c => c.parentId === form.stateId), v => set('cityId', v), () => setCityModal('new'), !form.countryId || !form.stateId)}</Field>
          <Field label="SIC Code"><input style={fInput(false)} value={form.sicCode ?? ''} onChange={e => set('sicCode', e.target.value)} /></Field>
        </Section>

        <Section sr={!!page} title="Account Management" legacyTitle="ACCOUNT MANAGEMENT" icon="💼">
          <Field label="Owner">{lookup(form.ownerId, owners, v => set('ownerId', v))}</Field>
          <Field label="Source Lead">{lookup(form.sourceLeadId, sourceLeads, v => set('sourceLeadId', v), () => setSourceLeadModalOpen(true))}</Field>
          <Field label="Credit Limit"><CurrencyInput value={form.creditLimit} onChange={n => set('creditLimit', n)} /></Field>
          <Field label="Preferred Contact Method">
            <AiCombo value={String(form.preferredContactMethod ?? 0)}
                     options={PREFERRED_CONTACT_METHODS.map((m, i) => ({ value: String(i), label: m }))}
                     onChange={v => set('preferredContactMethod', Number(v ?? 0))} />
          </Field>
          <Field label="Contract Date">{dateInput(form.contractDate, v => set('contractDate', v))}</Field>
          <Field label="Start Date">{dateInput(form.startDate, v => set('startDate', v))}</Field>
          <Field label="Maintenance Start Date">{dateInput(form.manintenanceStartDate, v => set('manintenanceStartDate', v))}</Field>
          <Field label="Maintenance End Date">{dateInput(form.manintenanceEndDate, v => set('manintenanceEndDate', v))}</Field>
          <Field label="Acceptance Date">{dateInput(form.acceptanceDate, v => set('acceptanceDate', v))}</Field>
          <Field label="Reminder Date">{dateInput(form.reminderDate, v => set('reminderDate', v))}</Field>
        </Section>

        <Section sr={!!page} title="Sending Preference" legacyTitle="Sending Preference" icon="✉️" grid={false}>
        <div style={{ display: 'flex', gap: 24 }}>
          <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12.5, cursor: 'pointer' }}>
            <input type="checkbox" checked={form.mail} onChange={e => set('mail', e.target.checked)} /> Mail
          </label>
          <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12.5, cursor: 'pointer' }}>
            <input type="checkbox" checked={form.sendEmail} onChange={e => set('sendEmail', e.target.checked)} /> Email
          </label>
        </div>
        </Section>

        <TabsCard sr={!!page}>
        <TabBar sr={!!page} tabs={page ? TABS : TABS.filter((x) => x.key !== 'relinquished' && x.key !== 'accounts')} tab={tab} onTab={(k) => { setTab(k); if (page) maintTabMemory.tab = k; }} />

        {/* Contact changes refresh the Primary Contact combo above (GoLive:
            its data source IS this client's Contacts collection). */}
        {isNew && (
          <div style={{ padding: 16, color: '#6b7280', fontSize: 12.5 }}>
            Save the client to add its contacts, projects, collectors, sample sites and notes.
          </div>
        )}
        {!isNew && tab === 'relinquished' && <RelinquishedTab clientId={clientId} clientName={detail?.customerName ?? ''} notify={notify} confirm={confirm} />}
        {!isNew && tab === 'accounts' && <AccountsTab clientId={clientId} clientName={detail?.customerName ?? ''} notify={notify} confirm={confirm} />}
        {!isNew && tab === 'contacts'   && <ContactsTab clientId={clientId} clientName={detail?.customerName ?? ''} type={0} notify={notify} confirm={confirm} onContactsChanged={() => void reloadContactOpts()} />}
        {!isNew && tab === 'invoicing'  && <ContactsTab clientId={clientId} clientName={detail?.customerName ?? ''} type={1} notify={notify} confirm={confirm} onContactsChanged={() => void reloadContactOpts()} />}
        {!isNew && tab === 'projects'   && <ProjectsTab clientId={clientId} clientName={detail?.customerName ?? ''} notify={notify} confirm={confirm} />}
        {!isNew && tab === 'collectors' && <CollectorsTab clientId={clientId} notify={notify} confirm={confirm} />}
        {!isNew && tab === 'sites'      && <SampleSitesTab clientId={clientId} notify={notify} confirm={confirm} />}
        {!isNew && tab === 'notes'      && <NotesTab clientId={clientId} clientName={detail?.customerName ?? ''} notify={notify} confirm={confirm} />}
        </TabsCard>
      </div>
    );

  const nestedEl = (
    <>
      {/* Country New/✎ → the full Country create/edit editor (nested State/City/Area).
          'new' opens a BLANK form (GoLive New button); 'edit' opens the selected
          country. On save, refresh countries + select it. */}
      {countryModal && (
        <CountryDetailModal
          countryId={countryModal === 'edit' ? (form?.countryId ?? null) : null}
          onClose={() => setCountryModal(false)}
          onSaved={async (savedId) => { setCountryModal(false); await loadGeo(); setForm(p => p ? { ...p, countryId: savedId, stateId: null, cityId: null } : p); }}
          notify={notify}
        />
      )}

      {/* Account ✎ → GoLive "Client Account Number" popup. On save: refresh the
          account list and select the new one. */}
      {accountModalOpen && (
        <AccountNumberModal clientId={clientId} clientName={detail?.customerName ?? ''}
          onClose={() => setAccountModalOpen(false)}
          onSaved={async (savedId) => { setAccountModalOpen(false); await reloadAccounts(); setForm(p => p ? { ...p, accountId: savedId } : p); }}
          notify={notify} />
      )}

      {/* Category ✎ → GoLive "Category" popup. */}
      {categoryModalOpen && (
        <CategoryModal
          onClose={() => setCategoryModalOpen(false)}
          onSaved={async (savedId) => { setCategoryModalOpen(false); await reloadCategories(); setForm(p => p ? { ...p, categoryId: savedId } : p); }}
          notify={notify} />
      )}

      {/* State New/✎ → the full GoLive "State" popup (State* / Short Name / Country +
          the nested City grid). 'new' = blank form under the current country,
          'edit' = the selected state. On save, refresh geo + select it. */}
      {stateModal && (
        <StateDetailModal stateId={stateModal === 'edit' ? (form?.stateId ?? null) : null} countryId={form?.countryId ?? null}
          onClose={() => setStateModal(false)}
          onSaved={async (savedId) => { setStateModal(false); await loadGeo(); setForm(p => p ? { ...p, stateId: savedId, cityId: null } : p); }}
          notify={notify} />
      )}

      {/* City New/✎ → the full GoLive "City" popup (City* / State + the nested Area grid).
          'new' = blank form under the current state, 'edit' = the selected city. */}
      {cityModal && (
        <CityDetailModal cityId={cityModal === 'edit' ? (form?.cityId ?? null) : null} stateId={form?.stateId ?? null}
          onClose={() => setCityModal(false)}
          onSaved={async (savedId) => { setCityModal(false); await loadGeo(); setForm(p => p ? { ...p, cityId: savedId } : p); }}
          notify={notify} />
      )}

      {/* (No Primary Contact ✎ editor — GoLive's PrimaryContact is a plain combo
          over the client's own Contacts; contacts are created on the Contacts tab.) */}

      {/* Source Lead ✎ → GoLive "Open Prospect" CRM form (creates a dbo.CRMProspects). */}
      {sourceLeadModalOpen && (
        <OpenProspectModal clientId={clientId}
          onClose={() => setSourceLeadModalOpen(false)}
          onSaved={async (savedId) => { setSourceLeadModalOpen(false); await reloadSourceLeads(); if (savedId) setForm(p => p ? { ...p, sourceLeadId: savedId } : p); }}
          notify={notify} />
      )}
    </>
  );

  // Full-page Detail View (standing rule 12): the Maintenance list is REPLACED by this page.
  if (page) {
    return (
      <MaintenanceDetailFrame
        entityLabel="Client" recordName={isNew ? 'New' : (detail?.customerName ?? '')} listTitle={page.listTitle}
        loading={loading || !form} formName="Client Master Data" isNew={isNew}
        validate={() => (form?.customerName?.trim() ? null : "'Client' must not be empty.")}
        persist={persist} onAfterSave={onSaved} onNew={page.onNew} actions={page}
        onHome={page.onHome} onClose={onClose}
        prevId={page.prevId} nextId={page.nextId} onNavigate={page.onNavigate}
        suspendKeys={!!(countryModal || accountModalOpen || categoryModalOpen || stateModal || cityModal || sourceLeadModalOpen)}
      >
        {bodyEl}
        {nestedEl}
      </MaintenanceDetailFrame>
    );
  }

  return (
    // Close on backdrop click ONLY when the click lands directly on this overlay —
    // NOT on a bubbled click from a nested popup (Country/State/City New/Edit modal).
    // Those nested modals stop onMouseDown but their close-X/Cancel fire onClick,
    // which would otherwise bubble here and wrongly close this form.
    <div onClick={e => { if (e.target === e.currentTarget) onClose(); }} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex,
                                     display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div onClick={e => e.stopPropagation()} {...modalKeys}
           style={{ background: '#fff', borderRadius: 8, width: max ? '96vw' : 1040, maxWidth: '96vw',
                    height: max ? '94vh' : '88vh', display: 'flex', flexDirection: 'column',
                    boxShadow: '0 10px 40px rgba(0,0,0,0.3)', border: '2px solid var(--brand-accent, #e8670a)' }}>
        <div style={{ padding: '10px 14px', fontWeight: 600, borderBottom: '1px solid #e5e7eb',
                      color: 'var(--ai-ink, #1f2430)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{detail ? `${detail.customerName} - Clients` : isNew ? 'Client - New' : 'Client'}</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button type="button" title={max ? 'Restore' : 'Maximize'} onClick={() => setMax(m => !m)}
                    style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 14, color: 'var(--brand-accent, #e8670a)', lineHeight: 1 }}><i className={`ti ${max ? 'ti-minimize' : 'ti-maximize'}`} /></button>
            <button type="button" title="Close" onClick={onClose}
                    style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 18, color: 'var(--brand-accent, #e8670a)', lineHeight: 1 }}><i className="ti ti-x" /></button>
          </span>
        </div>


        {bodyEl}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: 12, borderTop: '1px solid #e5e7eb' }}>
          <button type="button" className="ca-btn ca-btn-solid" disabled={saving || loading} onClick={() => void save()}>OK</button>
          <button type="button" className="ca-btn ca-btn-solid" onClick={onClose}>Cancel</button>
        </div>
      </div>

      {nestedEl}
    </div>
  );
}

// ── Account ✎ popup — GoLive "Client Account Number" (Customer* / Account
//    Number* / Default / Suspend). Customer is a combobox of all clients and,
//    like GoLive, opens EMPTY ("N/A") — it is a required field the user picks,
//    not one pre-filled with the client being edited. ─────────────────────────
function AccountNumberModal({ clientId, clientName, onClose, onSaved, notify }: {
  clientId: string; clientName: string; onClose: () => void; onSaved: (savedId: string) => void; notify: NotifyApi;
}) {
  const [customerId, setCustomerId] = useState<string | null>(null);   // GoLive: defaults to N/A
  const [clients, setClients] = useState<Opt[]>([{ id: clientId, name: clientName }]);
  const [accountNumber, setAccountNumber] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [suspend, setSuspend] = useState(false);
  const [saving, setSaving] = useState(false);

  // GoLive's Customer field lists every client — load them so it's a real lookup.
  useEffect(() => {
    void api.get('/api/v1/sample-receipt-notification/clients')
      .then(r => {
        const rows = (r.data as { data?: { oid: string; name: string }[] }).data ?? [];
        if (rows.length) setClients(rows.map(c => ({ id: c.oid, name: c.name })));
      })
      .catch(() => { /* keep the current client as the sole option */ });
  }, []);

  const save = useCallback(async () => {
    if (!customerId) { notify.warning('Customer is required.'); return; }
    if (!accountNumber.trim()) { notify.warning('Account Number is required.'); return; }
    setSaving(true);
    try {
      const res = await api.post(`/api/v1/clients/${encodeURIComponent(customerId)}/accounts`,
        { accountNumber, default: isDefault, suspend });
      notify.success('Saved successfully.');
      // Only re-select on the parent when the account was created for THIS client.
      onSaved(customerId === clientId ? ((res.data as { data?: { oid: string } }).data?.oid ?? '') : '');
    } catch (e) { notify.error(serverMessage(e) ?? 'Failed to save account number.'); }
    finally { setSaving(false); }
  }, [customerId, accountNumber, isDefault, suspend, clientId, notify, onSaved]);

  return (
    <div style={miniModal} onClick={onClose}>
      <MiniShell title="Client Account Number" onClose={onClose}
                 footer={<MiniOkCancel onOk={() => void save()} onCancel={onClose} saving={saving} />}>
        <div style={{ padding: '12px 0' }}>
          <div style={miniRow}><label style={{ fontSize: 11, color: '#4a4f58' }}>Customer:<span style={{ color: '#d33' }}>*</span></label>
            <AiCombo value={customerId} options={clients.map(o => ({ value: o.id, label: o.name }))}
                     onChange={setCustomerId} /></div>
          <div style={miniRow}><label style={{ fontSize: 11, color: '#4a4f58' }}>Account Number:<span style={{ color: '#d33' }}>*</span></label>
            <input style={fInput(!accountNumber.trim())} value={accountNumber} onChange={e => setAccountNumber(e.target.value)} /></div>
          <div style={miniRow}><span /><label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 11.5, cursor: 'pointer' }}>
            <input type="checkbox" checked={isDefault} onChange={e => setIsDefault(e.target.checked)} /> Default</label></div>
          <div style={miniRow}><span /><label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 11.5, cursor: 'pointer' }}>
            <input type="checkbox" checked={suspend} onChange={e => setSuspend(e.target.checked)} /> Suspend</label></div>
        </div>
      </MiniShell>
    </div>
  );
}

// ── Category ✎ popup — GoLive "Category" (Category* / Description). ──────────
function CategoryModal({ onClose, onSaved, notify }: {
  onClose: () => void; onSaved: (savedId: string) => void; notify: NotifyApi;
}) {
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);

  const save = useCallback(async () => {
    if (!category.trim()) { notify.warning('Category is required.'); return; }
    setSaving(true);
    try {
      const res = await api.post('/api/v1/clients/lookups/categories', { category, description });
      notify.success('Saved successfully.');
      onSaved((res.data as { data?: { oid: string } }).data?.oid ?? '');
    } catch (e) { notify.error(serverMessage(e) ?? 'Failed to save category.'); }
    finally { setSaving(false); }
  }, [category, description, notify, onSaved]);

  return (
    <div style={miniModal} onClick={onClose}>
      <MiniShell title="Category" onClose={onClose}
                 footer={<MiniOkCancel onOk={() => void save()} onCancel={onClose} saving={saving} />}>
        <div style={{ padding: '12px 0' }}>
          <div style={miniRow}><label style={{ fontSize: 11, color: '#4a4f58' }}>Category:<span style={{ color: '#d33' }}>*</span></label>
            <input style={fInput(!category.trim())} value={category} onChange={e => setCategory(e.target.value)} /></div>
          <div style={{ ...miniRow, alignItems: 'start' }}><label style={{ fontSize: 11 }}>Description:</label>
            <textarea rows={4} style={{ ...fInput(false), resize: 'vertical' }} value={description} onChange={e => setDescription(e.target.value)} /></div>
        </div>
      </MiniShell>
    </div>
  );
}

// NOTE: the local State / City mini-popups were REPLACED by the shared
// <StateDetailModal> / <CityDetailModal> (standing rule 8 — one implementation).
// GoLive's State popup carries the nested City grid and its City popup the nested
// Area grid; the mini-forms here had neither, so they were not to parity.

// ── Topic ✎ popup (Prospect) — GoLive "Topic" (Topic*). ─────────────────────
function TopicModal({ onClose, onSaved, notify }: {
  onClose: () => void; onSaved: (savedId: string) => void; notify: NotifyApi;
}) {
  const [topic, setTopic] = useState('');
  const [saving, setSaving] = useState(false);
  const save = useCallback(async () => {
    if (!topic.trim()) { notify.warning('Topic is required.'); return; }
    setSaving(true);
    try {
      const res = await api.post('/api/v1/clients/lookups/topics', { topic });
      notify.success('Saved successfully.');
      onSaved((res.data as { data?: { oid: string } }).data?.oid ?? '');
    } catch (e) { notify.error(serverMessage(e) ?? 'Failed to save topic.'); }
    finally { setSaving(false); }
  }, [topic, notify, onSaved]);
  return (
    <div style={miniModal} onClick={onClose}>
      <MiniShell title="Topic" onClose={onClose}
                 footer={<MiniOkCancel onOk={() => void save()} onCancel={onClose} saving={saving} />}>
        <div style={{ padding: '12px 0' }}>
          <div style={miniRow}><label style={{ fontSize: 11, color: '#4a4f58' }}>Topic:<span style={{ color: '#d33' }}>*</span></label>
            <input style={fInput(!topic.trim())} value={topic} onChange={e => setTopic(e.target.value)} /></div>
        </div>
      </MiniShell>
    </div>
  );
}

// ── Region ✎ popup (Prospect) — GoLive "Region" (Region Name* / Description). ─
function RegionModal({ onClose, onSaved, notify }: {
  onClose: () => void; onSaved: (savedId: string) => void; notify: NotifyApi;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const save = useCallback(async () => {
    if (!name.trim()) { notify.warning('Region Name is required.'); return; }
    setSaving(true);
    try {
      const res = await api.post('/api/v1/clients/lookups/regions', { name, description });
      notify.success('Saved successfully.');
      onSaved((res.data as { data?: { oid: string } }).data?.oid ?? '');
    } catch (e) { notify.error(serverMessage(e) ?? 'Failed to save region.'); }
    finally { setSaving(false); }
  }, [name, description, notify, onSaved]);
  return (
    <div style={miniModal} onClick={onClose}>
      <MiniShell title="Region" onClose={onClose}
                 footer={<MiniOkCancel onOk={() => void save()} onCancel={onClose} saving={saving} />}>
        <div style={{ padding: '12px 0' }}>
          <div style={miniRow}><label style={{ fontSize: 11, color: '#4a4f58' }}>Region Name:<span style={{ color: '#d33' }}>*</span></label>
            <input style={fInput(!name.trim())} value={name} onChange={e => setName(e.target.value)} /></div>
          <div style={{ ...miniRow, alignItems: 'start' }}><label style={{ fontSize: 11 }}>Description:</label>
            <textarea rows={3} style={{ ...fInput(false), resize: 'vertical' }} value={description} onChange={e => setDescription(e.target.value)} /></div>
        </div>
      </MiniShell>
    </div>
  );
}

// ── Source Lead ✎ → GoLive "Open Prospect" CRM form (dbo.CRMProspects) ───────
// Summary / Address / Contact / General sections + Quotes/Notes tabs (empty at
// create time, GoLive parity) + nested ✎ popups (Topic/Category/Region/Country/
// State/City). Probability/Rating/Status/Owner render for layout parity but have
// no CRMProspects column, so they are not persisted.
interface ProspectForm {
  topicId: string | null; prospects: string; clientCode: string; account: string; categoryId: string | null;
  clientNumber: string; fax: string; webSite: string; industryId: string | null;
  demoDate: string | null; quoteDate: string | null; probability: string; rating: string; followUpDate: string | null;
  street1: string; street2: string; regionId: string | null; zip: string;
  countryId: string | null; stateId: string | null; cityId: string | null;
  primaryContactId: string | null; officePhone: string; cellPhone: string; otherPhone: string; email: string;
  owner: string; status: string; remark: string;
}
function OpenProspectModal({ clientId, onClose, onSaved, notify }: {
  clientId: string; onClose: () => void; onSaved: (savedId: string) => void; notify: NotifyApi;
}) {
  const [max, setMax] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState<'quotes' | 'notes'>('quotes');
  const [f, setF] = useState<ProspectForm>({
    topicId: null, prospects: '', clientCode: '', account: '', categoryId: null, clientNumber: '', fax: '',
    webSite: '', industryId: null, demoDate: null, quoteDate: null, probability: '0', rating: 'Hot', followUpDate: null,
    street1: '', street2: '', regionId: null, zip: '', countryId: null, stateId: null, cityId: null,
    primaryContactId: null, officePhone: '', cellPhone: '', otherPhone: '', email: '', owner: 'Service', status: 'None', remark: '',
  });
  function setV<K extends keyof ProspectForm>(k: K, v: ProspectForm[K]) { setF(p => ({ ...p, [k]: v })); }

  const [topics, setTopics] = useState<Opt[]>([]);
  const [regions, setRegions] = useState<Opt[]>([]);
  const [industries, setIndustries] = useState<Opt[]>([]);
  const [categories, setCategories] = useState<Opt[]>([]);
  const [countries, setCountries] = useState<Opt[]>([]);
  const [states, setStates] = useState<Opt[]>([]);
  const [cities, setCities] = useState<Opt[]>([]);
  const [contacts, setContacts] = useState<Opt[]>([]);
  const [topicOpen, setTopicOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [regionOpen, setRegionOpen] = useState(false);
  const [countryOpen, setCountryOpen] = useState<false | 'new' | 'edit'>(false);
  const [stateOpen, setStateOpen] = useState<false | 'new' | 'edit'>(false);
  const [cityOpen, setCityOpen] = useState<false | 'new' | 'edit'>(false);

  const loadGeo = useCallback(async () => {
    try {
      const [cs, st, ci] = await Promise.all([
        api.get('/api/v1/system/states/countries'), api.get('/api/v1/cities/states'), api.get('/api/v1/cities'),
      ]);
      setCountries(((cs.data as { data?: { id: string; name: string }[] }).data ?? []).map(c => ({ id: c.id, name: c.name, parentId: null })));
      setStates(((st.data as { data?: { id: string; shortName: string; longName: string | null; countryId: string | null }[] }).data ?? []).map(s => ({ id: s.id, name: s.longName || s.shortName, parentId: s.countryId })));
      setCities(((ci.data as { data?: { id: string; cityName: string | null; stateId: string | null }[] }).data ?? []).map(c => ({ id: c.id, name: c.cityName ?? '', parentId: c.stateId })));
    } catch { /* geo optional */ }
  }, []);
  const fill = useCallback((url: string, setter: (v: Opt[]) => void) =>
    api.get(url).then(r => setter((r.data as { data?: Opt[] }).data ?? [])).catch(() => { /* leave empty */ }), []);
  useEffect(() => {
    void loadGeo();
    void fill('/api/v1/clients/lookups/topics', setTopics);
    void fill('/api/v1/clients/lookups/regions', setRegions);
    void fill('/api/v1/clients/lookups/industries', setIndustries);
    void fill('/api/v1/clients/lookups/categories', setCategories);
    void fill(`/api/v1/clients/${encodeURIComponent(clientId)}/contact-options`, setContacts);
  }, [clientId, loadGeo, fill]);

  const save = useCallback(async () => {
    if (!f.prospects.trim()) { notify.warning('Prospects is required.'); return; }
    if (!f.topicId) { notify.warning('Topic is required.'); return; }
    setSaving(true);
    try {
      const res = await api.post('/api/v1/clients/lookups/source-leads', {
        prospects: f.prospects, probability: f.probability, topicId: f.topicId, account: f.account, clientCode: f.clientCode,
        clientNumber: f.clientNumber, fax: f.fax, webSite: f.webSite, industryId: f.industryId, categoryId: f.categoryId,
        demoDate: f.demoDate, quoteDate: f.quoteDate, followUpDate: f.followUpDate, street1: f.street1, street2: f.street2,
        regionId: f.regionId, countryId: f.countryId, stateId: f.stateId, cityId: f.cityId, zip: f.zip,
        primaryContactId: f.primaryContactId, officePhone: f.officePhone, mobilePhone: f.cellPhone,
        otherPhone: f.otherPhone, email: f.email, remark: f.remark,
      });
      notify.success('Saved successfully.');
      onSaved((res.data as { data?: { oid: string } }).data?.oid ?? '');
    } catch (e) { notify.error(serverMessage(e) ?? 'Failed to save prospect.'); }
    finally { setSaving(false); }
  }, [f, notify, onSaved]);

  // Same lookup shape as the Client detail, incl. the parent-gated `disabled`
  // that also HIDES the New/Edit (✎) button (GoLive CRMProspects.cs carries the
  // identical Country.States / State.Cities DataSourceProperty(SelectNothing)
  // cascade, and reveals the editors one level at a time).
  // Same single GoLive "New" (+) button set as the main form's `lookup` (see there).
  const lk = (value: string | null, options: Opt[], onChange: (v: string | null) => void, onNew?: () => void, disabled = false): ReactNode => (
    <AiCombo value={value} options={options.map(o => ({ value: o.id, label: o.name }))}
             onChange={onChange} onNew={disabled ? undefined : onNew} newTitle="New" disabled={disabled} erasable />
  );
  const dt =(value: string | null, onChange: (v: string | null) => void): ReactNode => (
    <input type="date" className={`cdm-date${value ? '' : ' cdm-date-empty'}`} style={fInput(false)}
           value={value?.slice(0, 10) ?? ''} onChange={e => onChange(e.target.value || null)} />
  );

  return (
    <div onClick={e => { if (e.target === e.currentTarget) onClose(); }} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1350,
                                     display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div onClick={e => e.stopPropagation()}
           style={{ background: '#fff', borderRadius: 8, width: max ? '96vw' : 960, maxWidth: '96vw',
                    height: max ? '94vh' : '88vh', display: 'flex', flexDirection: 'column',
                    boxShadow: '0 10px 40px rgba(0,0,0,0.3)', border: '2px solid var(--brand-accent, #e8670a)' }}>
        <div style={{ padding: '10px 14px', fontWeight: 600, borderBottom: '1px solid #e5e7eb', color: 'var(--ai-ink, #1f2430)',
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Open Prospect</span>
          <span style={{ display: 'flex', gap: 12 }}>
            <button type="button" title={max ? 'Restore' : 'Maximize'} onClick={() => setMax(m => !m)}
                    style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 14, color: 'var(--brand-accent, #e8670a)' }}><i className={`ti ${max ? 'ti-minimize' : 'ti-maximize'}`} /></button>
            <button type="button" title="Close" onClick={onClose}
                    style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 18, color: 'var(--brand-accent, #e8670a)' }}><i className="ti ti-x" /></button>
          </span>
        </div>

        <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
          <style>{'.cdm-date.cdm-date-empty:not(:focus)::-webkit-datetime-edit{color:transparent}'}</style>
          <SectionHeading text="Summary" />
          <div style={sectionGrid}>
            <Field label="Topic" required>{lk(f.topicId, topics, v => setV('topicId', v), () => setTopicOpen(true))}</Field>
            <Field label="Web Site"><input style={fInput(false)} value={f.webSite} onChange={e => setV('webSite', e.target.value)} /></Field>
            <Field label="Prospects" required><input style={fInput(!f.prospects.trim())} value={f.prospects} onChange={e => setV('prospects', e.target.value)} /></Field>
            <Field label="Industry">{lk(f.industryId, industries, v => setV('industryId', v))}</Field>
            <Field label="Client Code"><input style={fInput(false)} value={f.clientCode} onChange={e => setV('clientCode', e.target.value)} /></Field>
            <Field label="Demo Date">{dt(f.demoDate, v => setV('demoDate', v))}</Field>
            <Field label="Account"><input style={fInput(false)} value={f.account} onChange={e => setV('account', e.target.value)} /></Field>
            <Field label="Quote Date">{dt(f.quoteDate, v => setV('quoteDate', v))}</Field>
            <Field label="Category">{lk(f.categoryId, categories, v => setV('categoryId', v), () => setCatOpen(true))}</Field>
            <Field label="Probability"><SpinInput value={f.probability} onChange={v => setV('probability', v)} /></Field>
            <Field label="Client Number"><input style={fInput(false)} value={f.clientNumber} onChange={e => setV('clientNumber', e.target.value)} /></Field>
            <Field label="Rating"><select style={fInput(false)} value={f.rating} onChange={e => setV('rating', e.target.value)}>{['Hot', 'Warm', 'Cold'].map(r => <option key={r} value={r}>{r}</option>)}</select></Field>
            <Field label="Fax"><input style={fInput(false)} value={f.fax} onChange={e => setV('fax', e.target.value)} /></Field>
            <Field label="Follow Up Date">{dt(f.followUpDate, v => setV('followUpDate', v))}</Field>
          </div>

          <SectionHeading text="Address" />
          <div style={sectionGrid}>
            <Field label="Address 1"><input style={fInput(false)} value={f.street1} onChange={e => setV('street1', e.target.value)} /></Field>
            <Field label="Country">{lk(f.countryId, countries, v => setF(p => ({ ...p, countryId: v, stateId: null, cityId: null })), () => setCountryOpen('new'))}</Field>
            <Field label="Address 2"><input style={fInput(false)} value={f.street2} onChange={e => setV('street2', e.target.value)} /></Field>
            <Field label="State">{lk(f.stateId, states.filter(s => s.parentId === f.countryId), v => setF(p => ({ ...p, stateId: v, cityId: null })), () => setStateOpen('new'), !f.countryId)}</Field>
            <Field label="Region">{lk(f.regionId, regions, v => setV('regionId', v), () => setRegionOpen(true))}</Field>
            <Field label="City">{lk(f.cityId, cities.filter(c => c.parentId === f.stateId), v => setV('cityId', v), () => setCityOpen('new'), !f.countryId || !f.stateId)}</Field>
            <Field label="Zip"><input style={fInput(false)} value={f.zip} onChange={e => setV('zip', e.target.value)} /></Field>
            <div />
          </div>

          <SectionHeading text="Contact" />
          <div style={sectionGrid}>
            <Field label="Primary Contact">{lk(f.primaryContactId, contacts, v => setV('primaryContactId', v))}</Field>
            <Field label="Other Phone"><input style={fInput(false)} value={f.otherPhone} onChange={e => setV('otherPhone', e.target.value)} /></Field>
            <Field label="Office Phone"><input style={fInput(false)} value={f.officePhone} onChange={e => setV('officePhone', e.target.value)} /></Field>
            <Field label="Email"><input style={fInput(false)} value={f.email} onChange={e => setV('email', e.target.value)} /></Field>
            <Field label="Cell Phone"><input style={fInput(false)} value={f.cellPhone} onChange={e => setV('cellPhone', e.target.value)} /></Field>
            <div />
          </div>

          <SectionHeading text="General" />
          <div style={sectionGrid}>
            <Field label="Owner"><input style={fReadOnly} value={f.owner} disabled /></Field>
            <Field label="Remark"><input style={fInput(false)} value={f.remark} onChange={e => setV('remark', e.target.value)} /></Field>
            <Field label="Status"><select style={fInput(false)} value={f.status} onChange={e => setV('status', e.target.value)}>{['None', 'Open', 'Won', 'Lost'].map(s => <option key={s} value={s}>{s}</option>)}</select></Field>
            <div />
          </div>

          {/* Quotes / Notes tabs — GoLive shows an empty grid (Count=0) at create time. */}
          <div style={{ display: 'flex', gap: 2, borderBottom: '1px solid #e5e7eb', marginBottom: 10 }}>
            {(['quotes', 'notes'] as const).map(t => (
              <button key={t} type="button" onClick={() => setTab(t)}
                      style={{ padding: '7px 14px', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', border: 'none',
                               borderBottom: tab === t ? '2px solid var(--brand-accent, #e8670a)' : '2px solid transparent',
                               background: 'transparent', color: tab === t ? 'var(--brand-accent, #e8670a)' : '#6b7280', textTransform: 'capitalize' }}>{t}</button>
            ))}
          </div>
          <ProspectChildGrid
            columns={tab === 'quotes'
              /* GoLive CRMProspects ▸ Quote nested list (CRMQuotes fields —
                 "IsGobalDiscount" keeps the legacy spelling, §5.2 quirks rule). */
              ? ['Date', 'QuoteID', 'Title', 'Amount', 'ExpirationDate', 'Status', 'IsGobalDiscount', 'OfficePhone']
              /* CRMProspects ▸ Notes — same shape as the Client Notes tab. */
              : ['Date', 'Title', 'Note', 'Created By']}
            what={tab === 'quotes' ? 'Quotes' : 'Notes'} notify={notify} />
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: 12, borderTop: '1px solid #e5e7eb' }}>
          <button type="button" className="ca-btn ca-btn-solid" disabled={saving} onClick={() => void save()}>OK</button>
          <button type="button" className="ca-btn ca-btn-solid" onClick={onClose}>Cancel</button>
        </div>
      </div>

      {topicOpen && <TopicModal onClose={() => setTopicOpen(false)} onSaved={async (id) => { setTopicOpen(false); await fill('/api/v1/clients/lookups/topics', setTopics); if (id) setV('topicId', id); }} notify={notify} />}
      {catOpen && <CategoryModal onClose={() => setCatOpen(false)} onSaved={async (id) => { setCatOpen(false); await fill('/api/v1/clients/lookups/categories', setCategories); if (id) setV('categoryId', id); }} notify={notify} />}
      {regionOpen && <RegionModal onClose={() => setRegionOpen(false)} onSaved={async (id) => { setRegionOpen(false); await fill('/api/v1/clients/lookups/regions', setRegions); if (id) setV('regionId', id); }} notify={notify} />}
      {countryOpen && <CountryDetailModal countryId={countryOpen === 'edit' ? f.countryId : null} onClose={() => setCountryOpen(false)} onSaved={async (id) => { setCountryOpen(false); await loadGeo(); setF(p => ({ ...p, countryId: id, stateId: null, cityId: null })); }} notify={notify} />}
      {stateOpen && <StateDetailModal stateId={stateOpen === 'edit' ? f.stateId : null} countryId={f.countryId} onClose={() => setStateOpen(false)} onSaved={async (id) => { setStateOpen(false); await loadGeo(); setF(p => ({ ...p, stateId: id, cityId: null })); }} notify={notify} />}
      {cityOpen && <CityDetailModal cityId={cityOpen === 'edit' ? f.cityId : null} stateId={f.stateId} onClose={() => setCityOpen(false)} onSaved={async (id) => { setCityOpen(false); await loadGeo(); if (id) setV('cityId', id); }} notify={notify} />}
    </div>
  );
}

// ── Shared field/section styling ───────────────────────────────────────────
const sectionGrid: CSSProperties = { display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 24, marginBottom: 14 };
const fInput = (highlight: boolean): CSSProperties => ({
  width: '100%', padding: '4px 7px', border: '1px solid #cbd5e1', borderRadius: 5, boxSizing: 'border-box',
  fontSize: 12, background: highlight ? '#fffbe7' : '#fff',
});
const fReadOnly: CSSProperties = { ...fInput(false), background: '#f3f4f6', color: '#4a4f58' };

function SectionHeading({ text }: { text: string }) {
  return <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--ai-ink, #1f2430)', letterSpacing: 0.4,
                        margin: '4px 0 10px', paddingBottom: 4, borderBottom: '1px solid #e5e7eb' }}>{text}</div>;
}

function Field({ label, required, children }: { label: string; required?: boolean; children: ReactNode }) {
  // Sample Registration layout (Maintenance full page): <label> and its control are direct children of the .sr-fgrid.
  if (useSrLayout()) return <><label className={required ? 'req' : undefined}>{label}</label>{children}</>;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '150px 1fr', alignItems: 'center', gap: 8, marginBottom: 7 }}>
      <label style={{ fontSize: 11, color: '#4a4f58' }}>{label}:{required && <span style={{ color: '#d33' }}>*</span>}</label>
      {children}
    </div>
  );
}

/** One group of fields: a Sample-Registration card on the Maintenance page, the original heading + grid in the popups. */
function Section({ sr, title, legacyTitle, icon, grid = true, children }: {
  sr: boolean; title: string; legacyTitle: string; icon: string; grid?: boolean; children: ReactNode;
}) {
  if (!sr) return <><SectionHeading text={legacyTitle} /><div style={grid ? sectionGrid : { marginBottom: 14 }}>{children}</div></>;
  return (
    <SrLayout.Provider value>
      <SrCard title={title} icon={icon}>{grid ? <div className="sr-fgrid">{children}</div> : children}</SrCard>
    </SrLayout.Provider>
  );
}

/** The tab strip + its grid, in a Sample-Registration card on the Maintenance page. */
function TabsCard({ sr, children }: { sr: boolean; children: ReactNode }) {
  if (!sr) return <>{children}</>;
  return (
    <SrSingleEdit.Provider value>
      <section className="sr-sec"><div className="sr-sec-b" style={{ paddingBottom: 6 }}>{children}</div></section>
    </SrSingleEdit.Provider>
  );
}

function TabBar({ sr, tabs, tab, onTab }: { sr: boolean; tabs: { key: TabKey; label: string }[]; tab: TabKey; onTab: (k: TabKey) => void }) {
  if (sr) {
    return (
      <div className="sr-tabbar" role="tablist">
        {tabs.map(x => (
          <button key={x.key} type="button" role="tab" aria-selected={tab === x.key}
                  className={`sr-tb${tab === x.key ? ' active' : ''}`} onClick={() => onTab(x.key)}>{x.label}</button>
        ))}
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', gap: 2, borderBottom: '1px solid #e5e7eb', marginBottom: 10 }}>
      {tabs.map(x => (
        <button key={x.key} type="button" onClick={() => onTab(x.key)}
                style={{ padding: '7px 14px', fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
                         border: 'none', borderBottom: tab === x.key ? '2px solid var(--brand-accent, #e8670a)' : '2px solid transparent',
                         background: 'transparent', color: tab === x.key ? 'var(--brand-accent, #e8670a)' : '#6b7280' }}>
          {x.label}
        </button>
      ))}
    </div>
  );
}

// GoLive Credit Limit is a currency SPIN-edit showing "$0.00" with up/down
// arrows — show the value currency-formatted when idle, the raw number while
// editing (so it stays typable), plus the ▲▼ spinner.
function CurrencyInput({ value, onChange }: { value: number | null; onChange: (n: number) => void }) {
  const [focused, setFocused] = useState(false);
  const [raw, setRaw] = useState('');
  const num = value ?? 0;
  const bump = (d: number) => onChange(Math.max(0, Math.round((num + d) * 100) / 100));
  const spinBtn: CSSProperties = { border: 'none', background: 'var(--brand-accent, var(--ai-orange))', cursor: 'pointer', fontSize: 8, lineHeight: 1, padding: '0 6px', flex: 1, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' };
  return (
    <div style={{ display: 'flex', alignItems: 'stretch' }}>
      <input
        style={{ ...fInput(false), borderTopRightRadius: 0, borderBottomRightRadius: 0, borderRight: 'none' }}
        inputMode="decimal"
        value={focused ? raw : `$${num.toFixed(2)}`}
        onFocus={() => { setRaw(num ? String(num) : ''); setFocused(true); }}
        onBlur={() => setFocused(false)}
        onChange={e => { const v = e.target.value.replace(/[^0-9.]/g, ''); setRaw(v); onChange(v === '' ? 0 : Number(v) || 0); }}
      />
      <div style={{ display: 'flex', flexDirection: 'column', border: '1px solid var(--brand-accent-hover, var(--ai-orange-hover))', borderTopRightRadius: 5, borderBottomRightRadius: 5, overflow: 'hidden' }}>
        <button type="button" tabIndex={-1} title="Increase" style={{ ...spinBtn, borderBottom: '1px solid var(--brand-accent-hover, var(--ai-orange-hover))' }} onClick={() => bump(1)}><SpinTri up /></button>
        <button type="button" tabIndex={-1} title="Decrease" style={spinBtn} onClick={() => bump(-1)}><SpinTri /></button>
      </div>
    </div>
  );
}

// Shared mini-popup title bar with the orange close chrome (GoLive popups carry
// window chrome; the bare title looked off — user report 2026-08-01).
/**
 * The Quotes / Notes child grid inside the Open Prospect popup. GoLive renders a
 * FULL nested ListView here even on a brand-new prospect: the New · Link · Unlink
 * · Edit · Delete · Export to toolbar (Unlink/Edit/Delete greyed with nothing
 * selected), the column header row with its filter chevrons, "No data to display",
 * and a "Count=0" footer. The prospect must exist before a quote or note can hang
 * off it, so New/Link say so rather than opening an editor that could not save.
 */
function ProspectChildGrid({ columns, what, notify }: { columns: string[]; what: string; notify: NotifyApi }) {
  const needsSave = () => notify.info(`Save the prospect first — ${what.toLowerCase()} attach to a saved record.`);
  // Header-only export (the child grid is empty until the prospect is saved).
  const doExport = (fmt: ExportFormat) => { void exportRows(fmt, `Prospect${what}`, columns, []); };
  const btn = (label: string, icon: string, iconCls: string, onClick?: () => void): ReactNode => (
    <button type="button" className="ca-btn" disabled={!onClick} onClick={onClick}
            style={onClick ? undefined : { color: '#a8adb6', cursor: 'not-allowed', borderColor: '#e5e7eb', background: '#fafbfc' }}>
      <span className={`tb-i ${onClick ? iconCls : 'tb-i-grey'}`}
            style={onClick ? undefined : { opacity: 0.55 }}><i className={`ti ${icon}`} /></span> {label}
    </button>
  );
  return (
    <div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
        {btn('New', 'ti-plus', 'tb-i-green', needsSave)}
        {btn('Link', 'ti-link', 'tb-i-green', needsSave)}
        {btn('Unlink', 'ti-unlink', 'tb-i-grey')}
        {btn('Edit', 'ti-pencil', 'tb-i-blue')}
        {btn('Delete', 'ti-trash', 'tb-i-red')}
        <ExportButton onExport={doExport} />
      </div>
      <div style={{ border: '1px solid #e5e7eb', borderRadius: 6, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: '#fafbfc' }}>
              <th style={{ width: 30, borderBottom: '1px solid #e5e7eb' }} />
              <th style={{ width: 34, borderBottom: '1px solid #e5e7eb' }} />
              {columns.map(c => (
                <th key={c} style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #e5e7eb', color: '#4a4f58', whiteSpace: 'nowrap' }}>
                  <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
                    {c}<span style={{ color: '#c3c9d2', fontSize: 11 }}><i className="ti ti-filter" /></span>
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr><td colSpan={columns.length + 2} style={{ padding: 14, textAlign: 'center', color: '#6b7280' }}>No data to display</td></tr>
          </tbody>
        </table>
        {/* GoLive's nested-list footer is a plain "Count=N", not the pager. */}
        <div style={{ padding: '5px 10px', borderTop: '1px solid #e5e7eb', background: '#fafbfc', fontSize: 11.5, color: '#4a4f58' }}>Count=0</div>
      </div>
    </div>
  );
}

const miniChromeBtn = (fontSize: number): CSSProperties => ({
  border: 'none', background: 'transparent', cursor: 'pointer', fontSize,
  color: 'var(--brand-accent, #e8670a)', lineHeight: 1,
});

/**
 * The shared shell for EVERY popup opened from a form — the dialog box plus the
 * GoLive title bar, which always carries BOTH the maximize/restore and the close
 * icon (GoLive puts that pair on every popup, right down to the one-field "Topic"
 * editor). Owning the maximized state here means a popup can never ship without
 * the chrome. The caller supplies the body as children and the action row via
 * `footer`; maximized, the body scrolls and the footer stays pinned at the bottom.
 *
 * The overlay stays with the caller: some popups render their own (`miniModal`),
 * others are rendered INTO a parent's overlay (the tab edit forms).
 */
function MiniShell({ title, onClose, footer, children, width }: {
  title: string; onClose: () => void; footer?: ReactNode; children: ReactNode;
  /** Override the default 480px box width (GoLive's wide 2-column dialogs). */
  width?: number;
}) {
  const [max, setMax] = useState(false);
  return (
    <div onClick={e => e.stopPropagation()}
         style={max
           ? { ...miniBox, width: '96vw', maxWidth: '96vw', height: '94vh', display: 'flex', flexDirection: 'column' }
           : { ...miniBox, ...(width ? { width } : {}) }}>
      <div style={{ ...miniHeader, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>{title}</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button type="button" title={max ? 'Restore' : 'Maximize'} onClick={() => setMax(m => !m)}
                  style={miniChromeBtn(14)}><i className={`ti ${max ? 'ti-minimize' : 'ti-maximize'}`} /></button>
          <button type="button" title="Close" onClick={onClose} style={miniChromeBtn(16)}><i className="ti ti-x" /></button>
        </span>
      </div>
      <div style={max ? { flex: 1, overflow: 'auto', minHeight: 0 } : undefined}>{children}</div>
      {footer && <div style={miniFooter}>{footer}</div>}
    </div>
  );
}

/* Prospects.Probability uses the SHARED spin editor (components/SpinInput, promoted
   from a private copy here 2026-11-03); the legacy BO declares it `int` with
   `[RuleRange(…, 0, 100)]`, so it is clamped 0–100 at the call site. */
/** White triangle on the themed spin button (an SVG, so it shows whatever icon font or text colour rule is in force). */
function SpinTri({ up }: { up?: boolean }) {
  return <svg width="8" height="5" viewBox="0 0 8 5" aria-hidden="true" style={{ display: 'block' }}><path d={up ? 'M0 5L4 0L8 5Z' : 'M0 0L4 5L8 0Z'} fill="#fff" /></svg>;
}

/** The OK / Cancel pair every mini popup's footer uses (GoLive solid-orange). */
function MiniOkCancel({ onOk, onCancel, saving }: { onOk: () => void; onCancel: () => void; saving?: boolean }) {
  return (
    <>
      <button type="button" className="ca-btn ca-btn-solid" disabled={saving} onClick={onOk}>OK</button>
      <button type="button" className="ca-btn ca-btn-solid" onClick={onCancel}>Cancel</button>
    </>
  );
}

// ── Shared "Export to" split-button (GoLive multi-format dropdown) ────────────
// One button + a portal dropdown listing every format GoLive offers. Portaled to
// body (fixed) so it can't be clipped by the grid's scroll box or the modal.
function ExportButton({ disabled, onExport }: { disabled?: boolean; onExport: (f: ExportFormat) => void }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (popRef.current?.contains(t) || btnRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);
  const openMenu = () => {
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    setPos({ top: Math.min(r.bottom + 2, window.innerHeight - 320), left: Math.min(r.left, window.innerWidth - 190) });
    setOpen(o => !o);
  };
  return (
    <>
      <button ref={btnRef} type="button" className="ca-btn" disabled={disabled} onClick={openMenu}
              onKeyDown={(e) => {
                // Esc closes only this menu — stop it bubbling to the modal's shared
                // Esc handler (standing rule 14).
                if (open && e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); setOpen(false); }
              }}
              style={disabled ? { color: '#a8adb6', cursor: 'not-allowed', borderColor: '#e5e7eb', background: '#fafbfc' } : undefined}>
        <span className={`tb-i ${disabled ? 'tb-i-grey' : 'tb-i-blue'}`} style={disabled ? { opacity: 0.55 } : undefined}><i className="ti ti-download" /></span> Export to <i className="ti ti-chevron-down" style={{ fontSize: 9, marginLeft: 2 }} />
      </button>
      {open && pos && createPortal(
        <div ref={popRef} style={{ position: 'fixed', top: pos.top, left: pos.left, width: 180, background: '#fff',
                 border: '1px solid #cbd5e1', borderRadius: 6, boxShadow: '0 8px 24px rgba(0,0,0,0.18)', zIndex: 2100, padding: '4px 0', fontSize: 12.5 }}>
          {EXPORT_FORMATS.map(f => (
            <div key={f.key} role="button" onClick={() => { setOpen(false); onExport(f.key); }}
                 style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '6px 12px', cursor: 'pointer', color: 'var(--ai-ink, #1f2430)' }}
                 onMouseEnter={e => (e.currentTarget.style.background = '#f1f3f7')}
                 onMouseLeave={e => (e.currentTarget.style.background = '#fff')}>
              <i className={`ti ${f.icon}`} style={{ color: 'var(--brand-accent, #e8670a)', fontSize: 15 }} /> {f.label}
            </div>
          ))}
        </div>, document.body)}
    </>
  );
}

// ── Generic tab grid (New/Edit/Delete/Export toolbar + table) ─────────────
type ConfirmFn = (opts: { title: string; message: string; tone?: 'default' | 'danger' }) => Promise<boolean>;

/** A tab-grid column. `editKey` names the row field the INLINE editor writes;
 *  a column without one stays read-only (greyed) during inline edit — that's how
 *  GoLive treats the computed name column, which has no single backing field. */
interface TabColumn<T> {
  header: string; render: (r: T) => ReactNode; editKey?: string;
  /** What the in-place editor draws for this column (default a wrapping text box). G5's row editor edits EVERY stored column:
   *  text, a DROPDOWN for a lookup, a date picker, a tick box. */
  editType?: 'text' | 'combo' | 'date' | 'check';
  /** The editor's starting value (a lookup's DISPLAY name, 'true' / 'false' for a tick box). Default: the row's `editKey` field. */
  editInit?: (r: T) => string;
  /** `combo`: the choices, which may depend on the draft of the row being edited. */
  options?: (r: T, draft: Record<string, string>) => string[];
  /** Cells to blank when this one changes (the cells that depend on it). */
  resets?: string[];
}

/** The in-place text editor of a tab grid: wraps and grows with its text instead of cutting a long value. */
function TabWrapText({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const ref = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [value]);
  return (
    <textarea ref={ref} rows={1} value={value}
              onChange={e => onChange(e.target.value.replace(/\r?\n/g, ' '))}
              onKeyDown={e => { if (e.key === 'Enter') e.preventDefault(); }}
              style={{ width: '100%', boxSizing: 'border-box', padding: '3px 6px', border: '1px solid #cbd5e1', borderRadius: 4, fontSize: 12,
                       resize: 'none', overflow: 'hidden', lineHeight: 1.3, fontFamily: 'inherit', overflowWrap: 'anywhere' }} />
  );
}

/** One in-place editor cell of a tab grid, by the column's `editType`. */
function TabEditCell<T>({ col, row, draft, setDraft }: {
  col: TabColumn<T>; row: T; draft: Record<string, string>; setDraft: (f: (d: Record<string, string>) => Record<string, string>) => void;
}) {
  const key = col.editKey as string;
  const v = draft[key] ?? '';
  const set = (nv: string) => setDraft(d => {
    const next = { ...d, [key]: nv };
    if (nv !== d[key]) for (const dep of col.resets ?? []) next[dep] = '';
    return next;
  });
  const box: CSSProperties = { width: '100%', boxSizing: 'border-box', padding: '3px 6px', border: '1px solid #cbd5e1', borderRadius: 4, fontSize: 12 };
  if (col.editType === 'combo') {
    const opts = col.options?.(row, draft) ?? [];
    return (
      <div style={{ minWidth: 112 }}>
        <AiCombo compact wrap value={v || null} placeholder="" naLabel="(none)"
                 options={[...(v && !opts.includes(v) ? [v] : []), ...opts].map(o => ({ value: o, label: o }))}
                 onChange={nv => set(nv ?? '')} />
      </div>
    );
  }
  if (col.editType === 'date') return <input type="date" value={v.slice(0, 10)} onChange={e => set(e.target.value)} style={box} />;
  if (col.editType === 'check') {
    return <input type="checkbox" checked={v === 'true'} onChange={e => set(String(e.target.checked))} style={{ display: 'block', margin: '0 auto' }} />;
  }
  return <TabWrapText value={v} onChange={set} />;
}

function TabGrid<T extends { oid: string }>({ columns, rows, loading, onNew: onNewIn, onEdit: onEditIn, onDelete: onDeleteIn, onInlineSave, onLink: onLinkIn, onUnlink: onUnlinkIn, exportName }: {
  columns: TabColumn<T>[];
  rows: T[]; loading: boolean; exportName: string;
  onNew: () => void; onEdit: (r: T) => void; onDelete: (r: T) => void;
  /** Commit an inline row edit. Absent ⇒ the inline pencil is not offered. */
  onInlineSave?: (row: T, patch: Record<string, string>) => Promise<void>;
  /** Link / Unlink (G5 nested-list actions): present only on a list that is a LINK to shared records (Relinquished By). */
  onLink?: () => void;
  onUnlink?: (rows: T[]) => void;
}) {
  // GoLive nested ListView: a leading checkbox column (multi-select) + TWO row
  // command icons — the 1st edits the row IN PLACE, the 2nd opens the full detail
  // popup; the toolbar's Edit/Delete act on the checked row(s); a pager footer
  // shows "Page X of Y (N items)" + page size; and EVERY column header carries
  // the Excel-style filter/search dropdown (GoLive's header chevron).
  // Maintenance page: a Refresh of the client page remounts this grid, so its ticks are kept in maintTabMemory (keyed by the tab's export name).
  const memTicks = useSrSingleEdit() ? maintTabMemory.ticks : null;
  const [checked, setChecked] = useState<Set<string>>(() => new Set(memTicks?.get(exportName) ?? []));
  // Set when the user starts New / Edit / Delete / Link / Unlink: the reload that follows clears the ticks. A plain Refresh keeps them.
  const actionPending = useRef(false);
  const act = <A extends unknown[]>(fn: ((...a: A) => void) | undefined) => (fn ? (...a: A) => { actionPending.current = true; fn(...a); } : undefined);
  const onNew = act(onNewIn)!; const onEdit = act(onEditIn)!; const onDelete = act(onDeleteIn)!; const onLink = act(onLinkIn); const onUnlink = act(onUnlinkIn);
  // Inline row edit (GoLive's 1st pencil): which row, its draft values, saving.
  const [inlineId, setInlineId] = useState<string | null>(null);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [savingInline, setSavingInline] = useState(false);

  // Every mutation is followed by a reload, so clearing the ticks when a reload
  // STARTS is what makes the checkbox drop after New / Edit / Delete — GoLive
  // leaves no row ticked once the action has been submitted.
  useEffect(() => {
    if (loading) {
      // Maintenance page: only a reload that follows an action clears the ticks (a Refresh keeps them); the popups clear on every reload.
      if (!memTicks || actionPending.current) setChecked(new Set());
      actionPending.current = false;
      setInlineId(null);
    }
  }, [loading]);
  useEffect(() => { memTicks?.set(exportName, new Set(checked)); }, [checked, memTicks, exportName]);
  // Once the rows are in, drop ticks for rows that no longer exist.
  useEffect(() => {
    if (!memTicks || loading) return;
    setChecked((prev) => { const next = new Set([...prev].filter((id) => rows.some((r) => r.oid === id))); return next.size === prev.size ? prev : next; });
  }, [rows, loading, memTicks]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  // Per-column value filter: header → the checked values (empty = no filter).
  const [colFilter, setColFilter] = useState<Record<string, string[]>>({});
  // Which header's popup is open + where to anchor it, and its own search box.
  const [openCol, setOpenCol] = useState<string | null>(null);
  const [colAnchor, setColAnchor] = useState<{ top: number; left: number } | null>(null);
  const [colSearch, setColSearch] = useState('');

  // Column cell text used for filtering/search + export. The tab columns render
  // plain strings/numbers; anything richer filters as blank rather than throwing.
  const cellText = (r: T, c: { render: (r: T) => ReactNode }): string => {
    const v = c.render(r);
    return typeof v === 'string' || typeof v === 'number' ? String(v) : '';
  };

  // Rows surviving EVERY column's checklist (a column with no picks passes all).
  const shown = rows.filter(r => columns.every(c => {
    const picks = colFilter[c.header];
    return !picks?.length || picks.includes(cellText(r, c));
  }));

  const totalPages = Math.max(1, Math.ceil(shown.length / pageSize));
  const curPage = Math.min(page, totalPages - 1);
  const pageRows = shown.slice(curPage * pageSize, (curPage + 1) * pageSize);
  const checkedRows = shown.filter(r => checked.has(r.oid));
  const allOnPage = pageRows.length > 0 && pageRows.every(r => checked.has(r.oid));

  // Distinct values offered by the open column's popup — computed from the rows
  // the OTHER columns' filters allow (so a column can always be widened again),
  // narrowed by that popup's own search box.
  const colValues = (() => {
    const col = columns.find(c => c.header === openCol);
    if (!col) return [] as string[];
    const base = rows.filter(r => columns.every(c =>
      c.header === openCol || !colFilter[c.header]?.length || colFilter[c.header]!.includes(cellText(r, c))));
    const q = colSearch.trim().toLowerCase();
    return Array.from(new Set(base.map(r => cellText(r, col))))
      .filter(v => !q || v.toLowerCase().includes(q))
      .sort((a, b) => a.localeCompare(b));
  })();

  const toggle = (oid: string) => setChecked(s => { const n = new Set(s); if (n.has(oid)) n.delete(oid); else n.add(oid); return n; });
  const toggleAll = () => setChecked(s => { const n = new Set(s); if (allOnPage) pageRows.forEach(r => n.delete(r.oid)); else pageRows.forEach(r => n.add(r.oid)); return n; });

  // Export what's on screen (the column filters applied), matching the app-wide
  // "export exactly what you see" convention. Multi-format like GoLive.
  const doExport = (fmt: ExportFormat) => {
    const headers = columns.map(c => c.header);
    const data = shown.map(r => columns.map(c => cellText(r, c)));
    void exportRows(fmt, exportName, headers, data);
  };

  // GoLive greys a toolbar action it can't run — icon AND caption, not just a
  // faded button — so Edit/Delete read as unavailable until a row is ticked.
  const tbBtn = (label: string, icon: string, iconCls: string, onClick: () => void, disabled = false): ReactNode => (
    <button type="button" className="ca-btn" disabled={disabled} onClick={onClick}
            style={disabled ? { color: '#a8adb6', cursor: 'not-allowed', borderColor: '#e5e7eb', background: '#fafbfc' } : undefined}>
      <span className={`tb-i ${disabled ? 'tb-i-grey' : iconCls}`}
            style={disabled ? { opacity: 0.55 } : undefined}>{icon === 'ti-plus' ? <i className="ti ti-plus ai-circ ai-circ-add" aria-hidden="true" /> : <i className={`ti ${icon}`} />}</span> {label}
    </button>
  );
  const cbCol: CSSProperties = { width: 30, textAlign: 'center', padding: '5px 4px' };
  const rowIconBtn: CSSProperties = { border: 'none', background: 'transparent', cursor: 'pointer', padding: '0 3px' };
  const pgBtn = (disabled: boolean): CSSProperties => ({ border: '1px solid #e5e7eb', background: '#fff', borderRadius: 4, width: 22, height: 22, cursor: disabled ? 'not-allowed' : 'pointer', color: disabled ? '#c3c9d2' : '#4a4f58' });

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
        {tbBtn('New', 'ti-plus', 'tb-i-green', onNew)}
        {onLink && tbBtn('Link', 'ti-link', 'tb-i-blue', onLink)}
        {onUnlink && tbBtn('Unlink', 'ti-unlink', 'tb-i-blue', () => onUnlink(checkedRows), checkedRows.length === 0)}
        {tbBtn('Edit', 'ti-pencil', 'tb-i-blue', () => { const r = checkedRows[0]; if (checkedRows.length === 1 && r) onEdit(r); }, checkedRows.length !== 1)}
        {tbBtn('Delete', 'ti-trash', 'tb-i-red', () => checkedRows[0] && onDelete(checkedRows[0]), checkedRows.length === 0)}
        <ExportButton disabled={rows.length === 0} onExport={doExport} />
      </div>
      <div style={{ border: '1px solid #e5e7eb', borderRadius: 6, overflow: 'hidden' }}>
        <div style={{ maxHeight: 240, overflowY: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: '#fafbfc', position: 'sticky', top: 0 }}>
                <th style={{ ...cbCol, borderBottom: '1px solid #e5e7eb' }}>
                  <input type="checkbox" data-unsaved-ignore checked={allOnPage} onChange={toggleAll} aria-label="Select all" style={{ display: 'block', margin: '0 auto' }} />
                </th>
                <th style={{ width: 56, borderBottom: '1px solid #e5e7eb' }} />
                {/* Every column gets GoLive's header filter/search chevron. The
                    popup is PORTALED (the grid body scrolls in a 240px box, so an
                    absolutely-positioned popup would be clipped). */}
                {columns.map(c => (
                  <th key={c.header} style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #e5e7eb', color: '#4a4f58', whiteSpace: 'nowrap' }}>
                    <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
                      {c.header}
                      <button type="button" title={`Search / filter ${c.header}`}
                              onClick={e => {
                                const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
                                setColAnchor({ top: Math.min(r.bottom + 2, window.innerHeight - 60), left: Math.min(r.left, window.innerWidth - 250) });
                                setColSearch('');
                                setOpenCol(o => (o === c.header ? null : c.header));
                              }}
                              style={{ border: 'none', background: 'transparent', cursor: 'pointer', lineHeight: 1, fontSize: 11,
                                       color: colFilter[c.header]?.length ? 'var(--brand-accent, #e8670a)' : '#9aa1ad' }}><i className="ti ti-filter" /></button>
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={columns.length + 2} style={{ padding: 14, textAlign: 'center', color: '#6b7280' }}>Loading…</td></tr>
              ) : shown.length === 0 ? (
                <tr><td colSpan={columns.length + 2} style={{ padding: 14, textAlign: 'center', color: '#6b7280' }}>
                  {rows.length === 0 ? 'No data to display' : 'No rows match the column filters.'}
                </td></tr>
              ) : pageRows.map(r => {
                const editing = inlineId === r.oid;
                return (
                <tr key={r.oid} onDoubleClick={() => { if (!editing) onEdit(r); }}
                    style={{ cursor: 'default', background: editing ? '#fffdf5' : checked.has(r.oid) ? '#fff3ea' : '#fff' }}>
                  <td style={cbCol}>
                    {!editing && <input type="checkbox" data-unsaved-ignore checked={checked.has(r.oid)} onChange={() => toggle(r.oid)} style={{ display: 'block', margin: '0 auto' }} />}
                  </td>
                  {/* TWO row commands (GoLive): 1st edits IN PLACE, 2nd opens the
                      detail popup. While inline, they become Save / Cancel. */}
                  <td style={{ padding: '3px 4px', whiteSpace: 'nowrap', textAlign: 'center' }}>
                    {editing ? (
                      <>
                        <button type="button" title="Save" className="tb-i tb-i-green" disabled={savingInline}
                                style={rowIconBtn}
                                onClick={() => {
                                  if (!onInlineSave) return;
                                  setSavingInline(true);
                                  void onInlineSave(r, draft)
                                    .then(() => setInlineId(null))
                                    .finally(() => setSavingInline(false));
                                }}><i className="ti ti-device-floppy" /></button>
                        <button type="button" title="Cancel" className="tb-i tb-i-grey" disabled={savingInline}
                                style={rowIconBtn} onClick={() => setInlineId(null)}><i className="ti ti-arrow-back-up" /></button>
                      </>
                    ) : (
                      <>
                        {/* G5 has BOTH: the in-grid row edit (pencil-in-square) and the detail edit (plain pencil). */}
                        {onInlineSave && (
                          <button type="button" title="Edit in place" className="tb-i tb-i-blue" style={rowIconBtn}
                                  onClick={() => {
                                    const d: Record<string, string> = {};
                                    columns.forEach(c => {
                                      if (!c.editKey) return;
                                      const raw = (r as Record<string, unknown>)[c.editKey];
                                      d[c.editKey] = c.editInit ? c.editInit(r)
                                        : c.editType === 'check' ? String(!!raw)
                                        : c.editType === 'date' ? String(raw ?? '').slice(0, 10)
                                        : String(raw ?? '');
                                    });
                                    setDraft(d); setInlineId(r.oid);
                                  }}><i className="ti ti-edit" /></button>
                        )}
                        <button type="button" title="Open" className="tb-i tb-i-blue" style={rowIconBtn}
                                onClick={() => onEdit(r)}><i className="ti ti-pencil" /></button>
                      </>
                    )}
                  </td>
                  {columns.map(c => (
                    <td key={c.header} style={{ padding: editing ? '2px 4px' : '5px 8px', borderBottom: '1px solid #f0f0f0' }}>
                      {editing
                        ? (c.editKey
                            ? <TabEditCell col={c} row={r} draft={draft} setDraft={setDraft} />
                            /* No backing field (e.g. the computed name) — GoLive shows it greyed. */
                            : <span style={{ color: '#9aa1ad' }}>{c.render(r)}</span>)
                        : c.render(r)}
                    </td>
                  ))}
                </tr>
              ); })}
            </tbody>
          </table>
        </div>
        {/* Pager footer — GoLive "Page X of Y (N items)" + prev/next + page size. */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 10px', borderTop: '1px solid #e5e7eb', fontSize: 11.5, color: '#4a4f58', background: '#fafbfc' }}>
          <span>Page {curPage + 1} of {totalPages} ({shown.length} item{shown.length === 1 ? '' : 's'})</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <button type="button" disabled={curPage <= 0} onClick={() => setPage(p => Math.max(0, p - 1))} style={pgBtn(curPage <= 0)}><i className="ti ti-chevron-left" /></button>
            {Array.from({ length: totalPages }, (_, i) => i).slice(0, 8).map(i => (
              <button key={i} type="button" onClick={() => setPage(i)} style={{ padding: '1px 7px', borderRadius: 4, cursor: 'pointer', border: `1px solid ${i === curPage ? 'var(--brand-accent, #e8670a)' : '#e5e7eb'}`, background: '#fff', color: i === curPage ? 'var(--brand-accent, #e8670a)' : '#4a4f58' }}>{i + 1}</button>
            ))}
            <button type="button" disabled={curPage >= totalPages - 1} onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} style={pgBtn(curPage >= totalPages - 1)}><i className="ti ti-chevron-right" /></button>
          </span>
          <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            Page size:
            <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(0); }} style={{ border: '1px solid #cbd5e1', borderRadius: 4, fontSize: 11.5, padding: '1px 4px' }}>
              {[20, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </span>
        </div>
      </div>

      {/* Column search / filter popup — GoLive's Excel-style header dropdown:
          a search box, (All), and the distinct-value checklist. Portaled so the
          grid's own scroll box can't clip it; a transparent backdrop dismisses. */}
      {openCol && colAnchor && createPortal(
        <>
          <div onMouseDown={() => setOpenCol(null)} style={{ position: 'fixed', inset: 0, zIndex: 1990 }} />
          <div style={{ position: 'fixed', top: colAnchor.top, left: colAnchor.left, width: 240, zIndex: 2000,
                        background: '#fff', border: '1px solid #cbd5e1', borderRadius: 6,
                        boxShadow: '0 6px 20px rgba(0,0,0,0.18)', padding: 8, fontSize: 12 }}>
            <input type="text" autoFocus value={colSearch} onChange={e => setColSearch(e.target.value)}
                   placeholder="Enter text to search…"
                   style={{ width: '100%', boxSizing: 'border-box', padding: '4px 7px', border: '1px solid #cbd5e1', borderRadius: 5, fontSize: 12, marginBottom: 6 }} />
            <label style={{ display: 'flex', gap: 6, alignItems: 'center', padding: '3px 4px', cursor: 'pointer' }}>
              <input type="checkbox" data-unsaved-ignore checked={!colFilter[openCol]?.length}
                     onChange={() => { setColFilter(f => ({ ...f, [openCol]: [] })); setPage(0); }} />
              (All)
            </label>
            <div style={{ maxHeight: 190, overflowY: 'auto' }}>
              {colValues.map(v => (
                <label key={v || '(blank)'} style={{ display: 'flex', gap: 6, alignItems: 'center', padding: '3px 4px', cursor: 'pointer' }}>
                  <input type="checkbox" data-unsaved-ignore checked={colFilter[openCol]?.includes(v) ?? false}
                         onChange={() => {
                           setColFilter(f => {
                             const cur = f[openCol] ?? [];
                             return { ...f, [openCol]: cur.includes(v) ? cur.filter(x => x !== v) : [...cur, v] };
                           });
                           setPage(0);
                         }} />
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v || '(Blanks)'}</span>
                </label>
              ))}
              {colValues.length === 0 && <div style={{ padding: '6px 4px', color: '#9aa1ad' }}>No values.</div>}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6, marginTop: 6, borderTop: '1px solid #eef0f3', paddingTop: 6 }}>
              <button type="button" className="ca-btn" style={{ padding: '2px 10px' }}
                      onClick={() => { setColFilter(f => ({ ...f, [openCol]: [] })); setPage(0); }}>Clear</button>
              <button type="button" className="ca-btn ca-btn-solid" style={{ padding: '2px 10px' }}
                      onClick={() => setOpenCol(null)}>Close</button>
            </div>
          </div>
        </>, document.body)}
    </div>
  );
}

const miniModal: CSSProperties = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1400, display: 'flex', alignItems: 'center', justifyContent: 'center' };
const miniBox: CSSProperties = { background: '#fff', borderRadius: 8, width: 480, maxWidth: '92vw', boxShadow: '0 10px 40px rgba(0,0,0,0.3)', border: '2px solid var(--brand-accent, #e8670a)' };
const miniHeader: CSSProperties = { padding: '10px 14px', fontWeight: 600, borderBottom: '1px solid #e5e7eb', color: 'var(--ai-ink, #1f2430)' };
const miniFooter: CSSProperties = { display: 'flex', justifyContent: 'flex-end', gap: 8, padding: 12, borderTop: '1px solid #e5e7eb' };
const miniRow: CSSProperties = { display: 'grid', gridTemplateColumns: '110px 1fr', alignItems: 'center', gap: 8, marginBottom: 8, padding: '0 14px' };

// ── Contacts / Invoicing Contact tab ───────────────────────────────────────
interface ContactRow { oid: string; fullName: string; firstName: string | null; lastName: string | null; email: string | null; emailCc: string | null; emailBcc: string | null; position: string | null; officePhone: string | null; mobilePhone: string | null; comment: string | null; type: number; }
function ContactsTab({ clientId, clientName, type, notify, confirm, onContactsChanged }: {
  clientId: string; clientName: string; type: number; notify: NotifyApi; confirm: ConfirmFn;
  /** Adding/removing a contact changes the Primary Contact combo's data source
   *  (GoLive Customer.ContactsDataSource = the client's own Contacts). */
  onContactsChanged?: () => void;
}) {
  const [rows, setRows] = useState<ContactRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<ContactRow | 'new' | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/api/v1/clients/${encodeURIComponent(clientId)}/contacts`, { params: { type } });
      setRows((res.data as { data: ContactRow[] }).data ?? []);
    } catch { notify.error('Failed to load contacts.'); }
    finally { setLoading(false); }
  }, [clientId, type, notify]);
  useEffect(() => { void load(); }, [load]);

  const remove = useCallback(async (r: ContactRow) => {
    const ok = await confirm({ title: 'Delete', message: `Delete contact "${r.fullName}"?`, tone: 'danger' });
    if (!ok) return;
    try { await api.delete(`/api/v1/clients/${encodeURIComponent(clientId)}/contacts/${r.oid}`); notify.success('Deleted successfully.'); void load(); onContactsChanged?.(); }
    catch (e) { notify.error(serverMessage(e) ?? 'Failed to delete contact.'); }
  }, [clientId, confirm, notify, load, onContactsChanged]);

  // Inline row save — the PUT takes the whole record, so merge the edited cells
  // over the row (First/Last name and Position are not inline-editable and ride
  // along unchanged). FirstName + Email are server-required.
  // Inline row save. The contact PUT is a FULL REPLACE — ToContactInput maps every
  // property straight from the body and the UPDATE SETs every column — so anything
  // the payload omits is written back as NULL/false. The grid row carries only the
  // six displayed columns, so sending it alone would wipe the address, the other
  // phone numbers, gender, birthday, account, Prospect and — worst of all — the
  // SampleReceiptNotification / ReportDelivery flags, silently dropping the contact
  // as a recipient. So: read the FULL record first, merge the edited cells over it,
  // and send the whole thing back.
  const inlineSave = useCallback(async (r: ContactRow, patch: Record<string, string>) => {
    if (!{ ...r, ...patch }.email?.trim()) { notify.warning(`${type === 1 ? 'Invoice' : 'Report'} Mail is required.`); return; }
    try {
      const res = await api.get(`/api/v1/clients/${encodeURIComponent(clientId)}/contacts/${r.oid}`);
      const full = (res.data as { data?: Record<string, unknown> }).data ?? {};
      await api.put(`/api/v1/clients/${encodeURIComponent(clientId)}/contacts/${r.oid}`, {
        ...full,          // every stored value, so nothing is blanked
        ...patch,         // the cells the user actually edited
        type,             // the tab decides Report (0) vs Invoice (1)
      });
      notify.success('Saved successfully.');
      void load(); onContactsChanged?.();
    } catch (e) { notify.error(serverMessage(e) ?? 'Failed to save contact.'); }
  }, [clientId, type, notify, load, onContactsChanged]);

  // GoLive column captions: Report* on the Contacts tab, Invoice* on the
  // Invoicing Contact tab — same dbo.Contact fields, different labels.
  const p = type === 1 ? 'Invoice' : 'Report';
  return (
    <>
      {/* The name column has NO editKey — it is the computed First + Last, so it
          stays greyed during inline edit exactly as GoLive shows it; the name is
          changed in the detail popup (2nd row icon). */}
      <TabGrid columns={[
        { header: `${p}Contact`, render: r => r.fullName },
        { header: `${p}Mail`, render: r => r.email ?? '', editKey: 'email' },
        { header: `${p}MailCC`, render: r => r.emailCc ?? '', editKey: 'emailCc' },
        { header: `${p}MailBCC`, render: r => r.emailBcc ?? '', editKey: 'emailBcc' },
        { header: `${p}Phone`, render: r => r.officePhone ?? '', editKey: 'officePhone' },
        { header: 'Comment', render: r => r.comment ?? '', editKey: 'comment' },
      ]} rows={rows} loading={loading} exportName={type === 1 ? 'InvoicingContacts' : 'ReportingContacts'}
         onNew={() => setEditing('new')} onEdit={setEditing} onDelete={r => void remove(r)}
         onInlineSave={inlineSave} />
      {editing && (
        <ContactDetailModal clientId={clientId} clientName={clientName} type={type} showClientOnNew
                            contactOid={editing === 'new' ? null : editing.oid}
                            onClose={() => setEditing(null)} onSaved={() => { setEditing(null); void load(); onContactsChanged?.(); }} notify={notify} />
      )}
    </>
  );
}

// NOTE: the 8-field ContactEditModal mini popup was REPLACED by the shared
// <ContactDetailModal> — GoLive's Contact dialog edits the FULL dbo.Contact
// record, which the mini form could not (standing rule 8: one implementation).

// ── Projects tab ────────────────────────────────────────────────────────
interface ProjectRow { oid: string; projectId: string; projectName: string | null; projectLocation: string | null; retiredDate: string | null; comment: string | null; }
function ProjectsTab({ clientId, clientName, notify, confirm }: { clientId: string; clientName: string; notify: NotifyApi; confirm: ConfirmFn }) {
  const [rows, setRows] = useState<ProjectRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<ProjectRow | 'new' | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await api.get(`/api/v1/clients/${encodeURIComponent(clientId)}/projects`); setRows((res.data as { data: ProjectRow[] }).data ?? []); }
    catch { notify.error('Failed to load projects.'); } finally { setLoading(false); }
  }, [clientId, notify]);
  useEffect(() => { void load(); }, [load]);

  const remove = useCallback(async (r: ProjectRow) => {
    const ok = await confirm({ title: 'Delete', message: `Delete project "${r.projectId}"?`, tone: 'danger' });
    if (!ok) return;
    try { await api.delete(`/api/v1/clients/${encodeURIComponent(clientId)}/projects/${r.oid}`); notify.success('Deleted successfully.'); void load(); }
    catch (e) { notify.error(serverMessage(e) ?? 'Failed to delete project.'); }
  }, [clientId, confirm, notify, load]);

  // Same full-replace hazard as contacts: the project PUT writes every column, so
  // Comment must ride along or an inline edit would blank it. The row now carries
  // it (ClientProjectRow.Comment) even though the grid does not display it.
  const inlineSave = useCallback(async (r: ProjectRow, patch: Record<string, string>) => {
    const merged = { ...r, ...patch };
    if (!merged.projectId?.trim()) { notify.warning('Project ID is required.'); return; }
    try {
      // The PUT replaces EVERY project column (category, report mail To/CC, city, overview …), which the grid row does not
      // carry — so read the whole record first and put the edited cells over it, or an in-place edit would blank them.
      const res = await api.get(`/api/v1/clients/${encodeURIComponent(clientId)}/projects/${r.oid}`);
      const full = (res.data as { data?: Record<string, unknown> }).data ?? {};
      await api.put(`/api/v1/clients/${encodeURIComponent(clientId)}/projects/${r.oid}`, {
        ...full,
        projectId: merged.projectId, projectName: merged.projectName, projectLocation: merged.projectLocation,
        retiredDate: patch.retiredDate ? patch.retiredDate.slice(0, 10) : null,
      });
      notify.success('Saved successfully.'); void load();
    } catch (e) { notify.error(serverMessage(e) ?? 'Failed to save project.'); }
  }, [clientId, notify, load]);

  return (
    <>
      <TabGrid columns={[
        { header: 'ProjectId', render: r => r.projectId, editKey: 'projectId' },
        { header: 'ProjectName', render: r => r.projectName ?? '', editKey: 'projectName' },
        { header: 'ProjectLocation', render: r => r.projectLocation ?? '', editKey: 'projectLocation' },
        { header: 'RetiredDate', render: r => (r.retiredDate ? fmtLocalDate(r.retiredDate) : ''), editKey: 'retiredDate', editType: 'date' },
      ]} rows={rows} loading={loading} exportName="Projects" onNew={() => setEditing('new')} onEdit={setEditing} onDelete={r => void remove(r)}
         onInlineSave={inlineSave} />
      {editing && (
        <ProjectDetailModal clientId={clientId} clientName={clientName}
                            projectOid={editing === 'new' ? null : editing.oid}
                            onClose={() => setEditing(null)} onSaved={() => { setEditing(null); void load(); }} notify={notify} />
      )}
    </>
  );
}

// NOTE: the 3-field ProjectEditForm was REPLACED by the shared <ProjectDetailModal>
// — GoLive's Project dialog edits the FULL dbo.Project record (standing rule 8).

// ── Collectors tab ──────────────────────────────────────────────────────
interface CollectorRow { oid: string; fullName: string; firstName: string | null; lastName: string | null; contactPhone: string | null; comment: string | null; isDefault: boolean; isFieldUser: boolean; dontShow: boolean; retiredDate: string | null; }
// ── Accounts tab (G5 Customer.AccountNumber over dbo.ClientAccountNumber) ─────────────────────────────────
// G5's rules, enforced by the API: (client, number) unique, one default per client, a suspended number is never the default,
// and a number a customer still uses as its active Account cannot be deleted.
interface AccountRow { oid: string; accountNumber: string | null; isDefault: boolean; suspend: boolean }

function AccountsTab({ clientId, clientName, notify, confirm }: { clientId: string; clientName: string; notify: NotifyApi; confirm: ConfirmFn }) {
  const [rows, setRows] = useState<AccountRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<AccountRow | 'new' | null>(null);
  const base = `/api/v1/clients/${encodeURIComponent(clientId)}/account-numbers`;

  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await api.get(base); setRows((res.data as { data: AccountRow[] }).data ?? []); }
    catch { notify.error('Failed to load Accounts.'); } finally { setLoading(false); }
  }, [base, notify]);
  useEffect(() => { void load(); }, [load]);

  const remove = useCallback(async (r: AccountRow) => {
    const ok = await confirm({ title: 'Delete', message: `Delete "${r.accountNumber ?? ''}"?`, tone: 'danger' });
    if (!ok) return;
    try { await api.delete(`${base}/${r.oid}`); notify.success('Deleted successfully.'); void load(); }
    catch (e) { notify.error(serverMessage(e) ?? 'Failed to delete.'); }
  }, [base, confirm, notify, load]);

  // In-place edit (G5 row editor): the number plus the Default / Suspend ticks, under the same rules as the popup — a suspended
  // number is never the default, and the server enforces the rest (unique number, one default per client).
  const inlineSave = useCallback(async (r: AccountRow, patch: Record<string, string>) => {
    const number = (patch.accountNumber ?? '').trim();
    if (!number) { notify.warning("'Account Number' must not be empty"); return; }
    const suspend = patch.suspend === 'true';
    try {
      await api.put(`${base}/${r.oid}`, { accountNumber: number, default: patch.isDefault === 'true' && !suspend, suspend });
      notify.success('Saved successfully.'); void load();
    } catch (e) { notify.error(serverMessage(e) ?? 'Failed to save.'); }
  }, [base, notify, load]);

  const tick = (v: boolean) => <input type="checkbox" checked={v} readOnly disabled style={{ margin: 0 }} />;
  return (
    <>
      <TabGrid columns={[
        { header: 'AccountNumber', render: r => r.accountNumber ?? '', editKey: 'accountNumber' },
        { header: 'Default', render: r => tick(r.isDefault), editKey: 'isDefault', editType: 'check' },
        { header: 'Suspend', render: r => tick(r.suspend), editKey: 'suspend', editType: 'check' },
      ]} rows={rows} loading={loading} exportName="Accounts"
      onNew={() => setEditing('new')} onEdit={setEditing} onDelete={r => void remove(r)} onInlineSave={inlineSave} />
      {editing && (
        <div style={miniModal} onClick={() => setEditing(null)}>
          <AccountEditForm base={base} clientId={clientId} clientName={clientName} row={editing === 'new' ? null : editing}
                           onClose={() => setEditing(null)} onSaved={() => { setEditing(null); void load(); }} notify={notify} />
        </div>
      )}
    </>
  );
}

/** G5's "Client Account Number" popup: Customer* lookup (N/A until picked), Account Number*, then Default / Suspend ticks sitting
 *  under the input. New files the account under the CHOSEN customer; an existing account keeps its customer (it is shown, not changed). */
function AccountEditForm({ base, clientId, clientName, row, onClose, onSaved, notify }: {
  base: string; clientId: string; clientName: string; row: AccountRow | null; onClose: () => void; onSaved: () => void; notify: NotifyApi;
}) {
  const [customerId, setCustomerId] = useState<string | null>(row ? clientId : null);
  const [clients, setClients] = useState<Opt[]>([{ id: clientId, name: clientName }]);
  const [number, setNumber] = useState(row?.accountNumber ?? '');
  const [isDefault, setIsDefault] = useState(row?.isDefault ?? false);
  const [suspend, setSuspend] = useState(row?.suspend ?? false);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    void api.get('/api/v1/sample-receipt-notification/clients')
      .then(r => {
        const rows = (r.data as { data?: { oid: string; name: string }[] }).data ?? [];
        if (rows.length) setClients(rows.map(c => ({ id: c.oid, name: c.name })));
      })
      .catch(() => { /* keep this client as the sole option */ });
  }, []);
  const save = useCallback(async () => {
    if (!customerId) { notify.warning("'Customer' must not be empty"); return; }
    if (!number.trim()) { notify.warning("'Account Number' must not be empty"); return; }
    setSaving(true);
    try {
      const body = { accountNumber: number, default: isDefault && !suspend, suspend };
      if (row) await api.put(`${base}/${row.oid}`, body);
      else await api.post(`/api/v1/clients/${encodeURIComponent(customerId)}/account-numbers`, body);
      notify.success('Saved successfully.'); onSaved();
    } catch (e) { notify.error(serverMessage(e) ?? 'Failed to save.'); } finally { setSaving(false); }
  }, [customerId, number, isDefault, suspend, row, base, notify, onSaved]);

  const tick: CSSProperties = { display: 'flex', gap: 6, alignItems: 'center', fontSize: 11.5, cursor: 'pointer' };
  return (
    <MiniShell title="Client Account Number" onClose={onClose}
               footer={<MiniOkCancel onOk={() => void save()} onCancel={onClose} saving={saving} />}>
      <div style={{ padding: '12px 0' }}>
        <div style={miniRow}><label style={{ fontSize: 11, color: '#4a4f58' }}>Customer:<span style={{ color: '#d33' }}>*</span></label>
          <AiCombo value={customerId} options={clients.map(o => ({ value: o.id, label: o.name }))} onChange={setCustomerId} disabled={!!row} erasable /></div>
        <div style={miniRow}><label style={{ fontSize: 11, color: '#4a4f58' }}>Account Number:<span style={{ color: '#d33' }}>*</span></label>
          <input style={fInput(!number.trim())} value={number} onChange={e => setNumber(e.target.value)} /></div>
        <div style={miniRow}><span /><label style={tick}>
          <input type="checkbox" checked={isDefault && !suspend} disabled={suspend} onChange={e => setIsDefault(e.target.checked)} /> Default</label></div>
        {/* G5: ticking Suspend clears Default — a suspended account can never be the default. */}
        <div style={miniRow}><span /><label style={tick}>
          <input type="checkbox" checked={suspend} onChange={e => { setSuspend(e.target.checked); if (e.target.checked) setIsDefault(false); }} /> Suspend</label></div>
      </div>
    </MiniShell>
  );
}

// ── Relinquished By tab (G5 Customer.BroughtByList over dbo.BroughtBy.Customer) ──────────────────────────
// New / Edit / Delete reuse the Settings ▸ Relinquished By master (same rules and audit); Link / Unlink only change which client the row is on.
interface RelinquishedRow { oid: string; name: string | null; code: string | null; retiredDate: string | null; comment: string | null }
interface RelinquishedLinkRow { oid: string; name: string | null; code: string | null; retiredDate: string | null; linkedClient: string | null; linkedClientOid: string | null }

function RelinquishedTab({ clientId, clientName, notify, confirm }: { clientId: string; clientName: string; notify: NotifyApi; confirm: ConfirmFn }) {
  const [rows, setRows] = useState<RelinquishedRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<RelinquishedRow | 'new' | null>(null);
  const [linkOpen, setLinkOpen] = useState(false);
  const base = `/api/v1/clients/${encodeURIComponent(clientId)}/relinquished`;

  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await api.get(base); setRows((res.data as { data: RelinquishedRow[] }).data ?? []); }
    catch { notify.error('Failed to load Relinquished By.'); } finally { setLoading(false); }
  }, [base, notify]);
  useEffect(() => { void load(); }, [load]);

  const unlink = useCallback(async (picked: RelinquishedRow[]) => {
    const ok = await confirm({ title: 'Unlink', tone: 'danger',
      message: picked.length === 1 ? `Unlink "${picked[0]?.name ?? ''}" from this client?` : `Unlink ${picked.length} records from this client?` });
    if (!ok) return;
    try { await api.post(`${base}/unlink`, { oids: picked.map(r => r.oid) }); notify.success('Unlinked.'); void load(); }
    catch (e) { notify.error(serverMessage(e) ?? 'Failed to unlink.'); }
  }, [base, confirm, notify, load]);

  const remove = useCallback(async (r: RelinquishedRow) => {
    const ok = await confirm({ title: 'Delete', message: `Delete "${r.name ?? ''}"?`, tone: 'danger' });
    if (!ok) return;
    try { await api.delete(`/api/v1/brought-by/${r.oid}`); notify.success('Deleted successfully.'); void load(); }
    catch (e) { notify.error(serverMessage(e) ?? 'Failed to delete.'); }
  }, [confirm, notify, load]);

  return (
    <>
      {/* G5's captions for this nested list (BroughtByName · BroughtByCode · RetiredDate). */}
      <TabGrid columns={[
        { header: 'BroughtByName', render: r => r.name ?? '' },
        { header: 'BroughtByCode', render: r => r.code ?? '' },
        { header: 'RetiredDate', render: r => (r.retiredDate ? fmtLocalDate(r.retiredDate) : '') },
      ]} rows={rows} loading={loading} exportName="Relinquished By"
      onNew={() => setEditing('new')} onLink={() => setLinkOpen(true)} onUnlink={r => void unlink(r)}
      onEdit={setEditing} onDelete={r => void remove(r)} />
      {editing && (
        <div style={miniModal} onClick={() => setEditing(null)}>
          <RelinquishedEditForm base={base} clientId={clientId} clientName={clientName} row={editing === 'new' ? null : editing}
                                onClose={() => setEditing(null)} onSaved={() => { setEditing(null); void load(); }} notify={notify} />
        </div>
      )}
      {linkOpen && (
        <RelinquishedLinkDialog base={base} clientId={clientId} clientName={clientName} onClose={() => setLinkOpen(false)}
                                onLinked={() => { setLinkOpen(false); void load(); }} notify={notify} />
      )}
    </>
  );
}

function RelinquishedEditForm({ base, clientId, clientName, row, ownerClientId, onClose, onSaved, notify }: {
  base: string; clientId: string; clientName: string; row: RelinquishedRow | null;
  /** Opened from the Link popup: the client the row is on now (null = none); unset = this client. */
  ownerClientId?: string | null;
  onClose: () => void; onSaved: () => void; notify: NotifyApi;
}) {
  const [name, setName] = useState(row?.name ?? '');
  const [code, setCode] = useState(row?.code ?? '');
  const [comment, setComment] = useState(row?.comment ?? '');
  const [retired, setRetired] = useState(row?.retiredDate?.slice(0, 10) ?? '');
  // G5's Client lookup: N/A on a new row (the nested-list New still files it under this client), the owning client on an existing one.
  const [client, setClient] = useState<string | null>(row ? (ownerClientId === undefined ? clientId : ownerClientId) : null);
  const [clients, setClients] = useState<Opt[]>([{ id: clientId, name: clientName }]);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    void api.get('/api/v1/sample-receipt-notification/clients')
      .then(r => {
        const rows = (r.data as { data?: { oid: string; name: string }[] }).data ?? [];
        if (rows.length) setClients(rows.map(c => ({ id: c.oid, name: c.name })));
      })
      .catch(() => { /* keep this client as the sole option */ });
  }, []);
  const save = useCallback(async () => {
    if (!name.trim()) { notify.warning("'Relinquished By must not be empty'"); return; }
    setSaving(true);
    try {
      const body = { broughtByName: name, broughtByCode: code, retiredDate: retired || null, comment };
      let oid = row?.oid;
      if (row) await api.put(`/api/v1/brought-by/${row.oid}`, body);
      else {
        // The tab's New creates the row AND puts it on this client; the master's create has no Retired Date, so set it after.
        const res = await api.post(base, { broughtByName: name, broughtByCode: code, comment });
        oid = (res.data as { data?: { oid?: string } }).data?.oid;
        if (oid && retired) await api.put(`/api/v1/brought-by/${oid}`, body);
      }
      // Client lookup: a different client moves the row there; clearing it on an existing row takes it off this client.
      if (oid && client && client.toLowerCase() !== clientId.toLowerCase())
        await api.post(`/api/v1/clients/${encodeURIComponent(client)}/relinquished/link`, { oids: [oid] });
      else if (oid && row && !client && (ownerClientId === undefined || ownerClientId))
        await api.post(`${base}/unlink`, { oids: [oid] });
      notify.success('Saved successfully.'); onSaved();
    } catch (e) { notify.error(serverMessage(e) ?? 'Failed to save.'); } finally { setSaving(false); }
  }, [name, code, comment, retired, client, clientId, row, base, notify, onSaved]);

  const cell: CSSProperties = { display: 'grid', gridTemplateColumns: '110px 1fr', alignItems: 'center', gap: 8 };
  return (
    <MiniShell title="Relinquished By" width={760} onClose={onClose}
               footer={<MiniOkCancel onOk={() => void save()} onCancel={onClose} saving={saving} />}>
      {/* G5 layout: Relinquished By · Code / Client · Retired Date / Comment across both columns. */}
      <div style={{ padding: '14px 14px 12px', display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 18, rowGap: 8 }}>
        <div style={cell}><label style={{ fontSize: 11, color: '#4a4f58' }}>Relinquished By:<span style={{ color: '#d33' }}>*</span></label><input style={fInput(!name.trim())} value={name} onChange={e => setName(e.target.value)} /></div>
        <div style={cell}><label style={{ fontSize: 11 }}>Relinquished Code:</label><input style={fInput(false)} value={code} onChange={e => setCode(e.target.value)} /></div>
        <div style={cell}><label style={{ fontSize: 11 }}>Client:</label>
          <AiCombo value={client} options={clients.map(o => ({ value: o.id, label: o.name }))} onChange={v => setClient(v)} erasable /></div>
        <div style={cell}><label style={{ fontSize: 11 }}>Retired Date:</label><input type="date" style={fInput(false)} value={retired} onChange={e => setRetired(e.target.value)} /></div>
        <div style={{ ...cell, gridColumn: '1 / -1', alignItems: 'start', gridTemplateColumns: '110px 1fr' }}><label style={{ fontSize: 11 }}>Comment:</label><textarea rows={4} style={{ ...fInput(false), resize: 'vertical' }} value={comment} onChange={e => setComment(e.target.value)} /></div>
      </div>
    </MiniShell>
  );
}

/** The Link popup (G5 nested-list Link): the Relinquished By rows that are not on this client; ticking and OK puts them on it. */
function RelinquishedLinkDialog({ base, clientId, clientName, onClose, onLinked, notify }: {
  base: string; clientId: string; clientName: string; onClose: () => void; onLinked: () => void; notify: NotifyApi;
}) {
  const [rows, setRows] = useState<RelinquishedLinkRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Set<string | number>>(new Set());
  const [saving, setSaving] = useState(false);
  // G5's popup carries its own New and a pencil on every row, both opening the Relinquished By form.
  const [editing, setEditing] = useState<{ row: RelinquishedRow | null; owner: string | null | undefined } | null>(null);
  const load = useCallback(() => {
    setLoading(true);
    api.get(`${base}/linkable`).then(r => setRows((r.data as { data?: RelinquishedLinkRow[] }).data ?? []))
      .catch(() => notify.error('Failed to load Relinquished By.')).finally(() => setLoading(false));
  }, [base, notify]);
  useEffect(() => { load(); }, [load]);
  // G5 lists just the name here (caption BroughtBy).
  const columns: AppColumn<RelinquishedLinkRow>[] = [
    { field: 'name', header: 'BroughtBy', width: 560 },
  ];
  const openRow = async (r: RelinquishedLinkRow) => {
    try {
      const d = ((await api.get(`/api/v1/brought-by/${r.oid}`)).data as { data?: { comment?: string | null } }).data;
      setEditing({ row: { oid: r.oid, name: r.name, code: r.code, retiredDate: r.retiredDate, comment: d?.comment ?? '' }, owner: r.linkedClientOid?.toLowerCase() ?? null });
    } catch (e) { notify.error(serverMessage(e) ?? 'Failed to load.'); }
  };
  const link = async () => {
    if (selected.size === 0) return;
    setSaving(true);
    try { await api.post(`${base}/link`, { oids: [...selected] }); notify.success('Linked.'); onLinked(); }
    catch (e) { notify.error(serverMessage(e) ?? 'Failed to link.'); } finally { setSaving(false); }
  };
  return (
    <AppDialog open title="Brought By" icon="ti-link" width={760} onClose={onClose}
      footer={(
        <>
          <button className="ca-btn ca-btn-solid" disabled={selected.size === 0 || saving} onClick={() => void link()}>OK</button>
          <button className="ca-btn" onClick={onClose}>Cancel</button>
        </>
      )}>
      <div style={{ marginBottom: 10 }}>
        <button type="button" className="ca-btn ca-btn-solid" onClick={() => setEditing({ row: null, owner: undefined })}>New</button>
      </div>
      <AppDataGrid<RelinquishedLinkRow>
        rows={rows} columns={columns} getRowId={r => r.oid} loading={loading}
        selectable selectedIds={selected} onSelectionChange={setSelected}
        onRowOpen={r => void openRow(r)}
        defaultPageSize={20} emptyText="No data to display" hideHeaderTools />
      {editing && (
        <div style={miniModal} onClick={() => setEditing(null)}>
          <RelinquishedEditForm base={base} clientId={clientId} clientName={clientName} row={editing.row} ownerClientId={editing.owner}
                                onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} notify={notify} />
        </div>
      )}
    </AppDialog>
  );
}

function CollectorsTab({ clientId, notify, confirm }: { clientId: string; notify: NotifyApi; confirm: ConfirmFn }) {
  const [rows, setRows] = useState<CollectorRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<CollectorRow | 'new' | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await api.get(`/api/v1/clients/${encodeURIComponent(clientId)}/collectors`); setRows((res.data as { data: CollectorRow[] }).data ?? []); }
    catch { notify.error('Failed to load collectors.'); } finally { setLoading(false); }
  }, [clientId, notify]);
  useEffect(() => { void load(); }, [load]);

  const remove = useCallback(async (r: CollectorRow) => {
    const ok = await confirm({ title: 'Delete', message: `Delete collector "${r.fullName}"?`, tone: 'danger' });
    if (!ok) return;
    try { await api.delete(`/api/v1/clients/${encodeURIComponent(clientId)}/collectors/${r.oid}`); notify.success('Deleted successfully.'); void load(); }
    catch (e) { notify.error(serverMessage(e) ?? 'Failed to delete collector.'); }
  }, [clientId, confirm, notify, load]);

  // GoLive Collectors grid: IsDefault / IsFieldUser / DontShow are inline
  // CHECKBOX columns (toggling one PUTs the row) — the whole record rides along
  // so the un-edited fields are preserved.
  const toggleFlag = useCallback(async (r: CollectorRow, key: 'isDefault' | 'isFieldUser' | 'dontShow') => {
    try {
      await api.put(`/api/v1/clients/${encodeURIComponent(clientId)}/collectors/${r.oid}`, {
        firstName: r.firstName ?? '', lastName: r.lastName, contactPhone: r.contactPhone, comment: r.comment,
        isDefault: r.isDefault, isFieldUser: r.isFieldUser, dontShow: r.dontShow, [key]: !r[key],
      });
      void load();
    } catch (e) { notify.error(serverMessage(e) ?? 'Failed to update collector.'); }
  }, [clientId, load, notify]);
  const chk = (r: CollectorRow, key: 'isDefault' | 'isFieldUser' | 'dontShow'): ReactNode => (
    <input type="checkbox" checked={r[key]} onChange={() => void toggleFlag(r, key)}
           onClick={e => e.stopPropagation()} style={{ display: 'block', margin: '0 auto' }} />
  );
  // In-place edit (G5 row editor): every stored column of the row in one go, saved together.
  const inlineSave = useCallback(async (r: CollectorRow, patch: Record<string, string>) => {
    const firstName = (patch.firstName ?? r.firstName ?? '').trim();
    if (!firstName) { notify.warning('First Name is required.'); return; }
    try {
      await api.put(`/api/v1/clients/${encodeURIComponent(clientId)}/collectors/${r.oid}`, {
        firstName, lastName: patch.lastName ?? r.lastName, contactPhone: patch.contactPhone ?? r.contactPhone, comment: patch.comment ?? r.comment,
        isDefault: patch.isDefault === 'true', isFieldUser: patch.isFieldUser === 'true', dontShow: patch.dontShow === 'true',
      });
      notify.success('Saved successfully.'); void load();
    } catch (e) { notify.error(serverMessage(e) ?? 'Failed to save collector.'); }
  }, [clientId, notify, load]);
  return (
    <>
      <TabGrid columns={[
        { header: 'FullName', render: r => r.fullName },
        { header: 'FirstName', render: r => r.firstName ?? '', editKey: 'firstName' },
        { header: 'LastName', render: r => r.lastName ?? '', editKey: 'lastName' },
        { header: 'ContactPhone', render: r => r.contactPhone ?? '', editKey: 'contactPhone' },
        { header: 'Comment', render: r => r.comment ?? '', editKey: 'comment' },
        { header: 'IsDefault', render: r => chk(r, 'isDefault'), editKey: 'isDefault', editType: 'check' },
        { header: 'IsFieldUser', render: r => chk(r, 'isFieldUser'), editKey: 'isFieldUser', editType: 'check' },
        { header: 'DontShow', render: r => chk(r, 'dontShow'), editKey: 'dontShow', editType: 'check' },
        { header: 'RetiredDate', render: r => (r.retiredDate ? fmtLocalDate(r.retiredDate) : '') },
      ]} rows={rows} loading={loading} exportName="Collectors" onNew={() => setEditing('new')} onEdit={setEditing} onDelete={r => void remove(r)}
         onInlineSave={inlineSave} />
      {editing && (
        <div style={miniModal} onClick={() => setEditing(null)}>
          <CollectorEditForm clientId={clientId} row={editing === 'new' ? null : editing}
                              onClose={() => setEditing(null)} onSaved={() => { setEditing(null); void load(); }} notify={notify} />
        </div>
      )}
    </>
  );
}

function CollectorEditForm({ clientId, row, onClose, onSaved, notify }: {
  clientId: string; row: CollectorRow | null; onClose: () => void; onSaved: () => void; notify: NotifyApi;
}) {
  const [firstName, setFirstName] = useState(row?.firstName ?? '');
  const [lastName, setLastName] = useState(row?.lastName ?? '');
  const [phone, setPhone] = useState(row?.contactPhone ?? '');
  const [comment, setComment] = useState(row?.comment ?? '');
  // Preserved on edit (grid checkbox columns; the popup itself matches GoLive's
  // Collector dialog — Full Name / First Name* / Last Name / Contact Phone / Comment).
  const [isDefault] = useState(row?.isDefault ?? false);
  const [isFieldUser] = useState(row?.isFieldUser ?? false);
  const [dontShow] = useState(row?.dontShow ?? false);
  const [saving, setSaving] = useState(false);
  const fullName = `${firstName} ${lastName}`.trim();
  const save = useCallback(async () => {
    if (!firstName.trim()) { notify.warning('First Name is required.'); return; }
    setSaving(true);
    try {
      const body = { firstName, lastName, contactPhone: phone, comment, isDefault, isFieldUser, dontShow };
      if (row) await api.put(`/api/v1/clients/${encodeURIComponent(clientId)}/collectors/${row.oid}`, body);
      else await api.post(`/api/v1/clients/${encodeURIComponent(clientId)}/collectors`, body);
      notify.success('Saved successfully.'); onSaved();
    } catch (e) { notify.error(serverMessage(e) ?? 'Failed to save collector.'); } finally { setSaving(false); }
  }, [firstName, lastName, phone, comment, isDefault, isFieldUser, dontShow, row, clientId, notify, onSaved]);

  return (
    <MiniShell title={row ? 'Edit Collector' : 'New Collector'} onClose={onClose}
               footer={<MiniOkCancel onOk={() => void save()} onCancel={onClose} saving={saving} />}>
      <div style={{ padding: '12px 0' }}>
        <div style={miniRow}><label style={{ fontSize: 11 }}>Full Name</label><input style={{ ...fInput(false), background: '#f3f4f6', color: '#4a4f58' }} value={fullName} readOnly /></div>
        <div style={miniRow}><label style={{ fontSize: 11, color: '#4a4f58' }}>First Name:<span style={{ color: '#d33' }}>*</span></label><input style={fInput(!firstName.trim())} value={firstName} onChange={e => setFirstName(e.target.value)} /></div>
        <div style={miniRow}><label style={{ fontSize: 11 }}>Last Name</label><input style={fInput(false)} value={lastName} onChange={e => setLastName(e.target.value)} /></div>
        <div style={miniRow}><label style={{ fontSize: 11 }}>Contact Phone</label><input style={fInput(false)} value={phone} onChange={e => setPhone(e.target.value)} /></div>
        <div style={{ ...miniRow, alignItems: 'start' }}><label style={{ fontSize: 11 }}>Comment</label><textarea rows={3} style={{ ...fInput(false), resize: 'vertical' }} value={comment} onChange={e => setComment(e.target.value)} /></div>
      </div>
    </MiniShell>
  );
}

// ── Sample Sites tab ────────────────────────────────────────────────────
// Full GoLive Customer_SampleSites_ListView row (42 columns). FK columns carry
// their resolved display name; the writable ids (projectId/waterTypeId/pwsId/
// pwsNameId/collector) + address + siteUserDefinedColumn1 ride along so an inline
// edit round-trips without wiping them.
interface SiteRow {
  oid: string;
  siteID: string | null; siteName: string; siteCode: string | null; keyMap: string | null;
  samplePointID: string | null; blended: boolean; serviceArea: string | null; description: string | null;
  pwsName: string | null; projectIdCode: string | null; systemType: string | null; projectName: string | null;
  pwsIdCode: string | null; collectorName: string | null; isActive: boolean; siteNameArchived: string | null;
  monitoryingRequirement: string | null; facilityID: string | null; facilityName: string | null; facilityType: string | null;
  samplePointType: string | null; waterTypeName: string | null; city: string | null; state: string | null; zipCode: string | null;
  sampleType: string | null; samplingAddress: string | null; stationLocation: string | null; depth: string | null;
  sampleID: string | null; sampleNo: string | null; latitude: string | null; longitude: string | null;
  sampleMatrix: string | null; matrix: string | null; qty: number | null; collectorPhone: string | null;
  parentSampleID: string | null; parentSampleDate: string | null; repeatLocation: string | null; rejectionCriteria: string | null;
  // Writable ids kept for inline-edit round-trip (not shown as columns):
  address: string | null; projectId: string | null; waterTypeId: string | null;
  pwsId: string | null; pwsNameId: string | null; collector: string | null; siteUserDefinedColumn1: string | null;
}
/** FULL Sample Site record (GET .../sample-sites/{oid}) — adds resolved names not on the list row. */
interface SiteDetail extends SiteRow {
  clientId: string | null; clientName: string | null;
  pwsIdName: string | null; pwsNameName: string | null;
}
function SampleSitesTab({ clientId, notify, confirm }: { clientId: string; notify: NotifyApi; confirm: ConfirmFn }) {
  const [rows, setRows] = useState<SiteRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<SiteRow | 'new' | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await api.get(`/api/v1/clients/${encodeURIComponent(clientId)}/sample-sites`); setRows((res.data as { data: SiteRow[] }).data ?? []); }
    catch { notify.error('Failed to load sample sites.'); } finally { setLoading(false); }
  }, [clientId, notify]);
  useEffect(() => { void load(); }, [load]);

  // The lookups behind the row editor's dropdowns — the same lists the Sample Site popup binds to.
  const [pwsNames, setPwsNames] = useState<Opt[]>([]);
  const [pwsIds, setPwsIds] = useState<Opt[]>([]);
  const [waterTypes, setWaterTypes] = useState<Opt[]>([]);
  const [collectors, setCollectors] = useState<Opt[]>([]);
  useEffect(() => {
    const fill = (url: string, set: (v: Opt[]) => void) => { void api.get(url).then(r => set((r.data as { data?: Opt[] }).data ?? [])).catch(() => { /* leave empty */ }); };
    fill('/api/v1/clients/lookups/pws-names', setPwsNames);
    fill('/api/v1/clients/lookups/pws-ids', setPwsIds);
    fill('/api/v1/clients/lookups/water-types', setWaterTypes);
    fill(`/api/v1/clients/${encodeURIComponent(clientId)}/collector-options`, setCollectors);
  }, [clientId]);

  const remove = useCallback(async (r: SiteRow) => {
    const ok = await confirm({ title: 'Delete', message: `Delete site "${r.siteName}"?`, tone: 'danger' });
    if (!ok) return;
    try { await api.delete(`/api/v1/clients/${encodeURIComponent(clientId)}/sample-sites/${r.oid}`); notify.success('Deleted successfully.'); void load(); }
    catch (e) { notify.error(serverMessage(e) ?? 'Failed to delete site.'); }
  }, [clientId, confirm, notify, load]);

  // The row carries EVERY column the write needs, so merging the edited cells
  // over it is a complete payload — no extra fetch required (unlike contacts,
  // whose grid row is a subset). The PUT is a full replace.
  const inlineSave = useCallback(async (r: SiteRow, patch: Record<string, string>) => {
    // The dropdown cells hold DISPLAY names: an untouched one keeps its stored id, a changed one is mapped back to its id.
    const idFor = (list: Opt[], label: string | undefined, current: string | null): string | null =>
      label === undefined ? current : label === '' ? null : (list.find(o => o.name === label)?.id ?? current);
    const { pwsName: _pn, pwsIdCode: _pi, waterTypeName: _wt, collectorName: _cn, blended, isActive, ...text } = patch;
    const merged = {
      ...r, ...text,
      blended: blended === 'true', isActive: isActive === 'true',
      pwsNameId: idFor(pwsNames, patch.pwsName, r.pwsNameId), pwsId: idFor(pwsIds, patch.pwsIdCode, r.pwsId),
      waterTypeId: idFor(waterTypes, patch.waterTypeName, r.waterTypeId), collector: idFor(collectors, patch.collectorName, r.collector),
    };
    if (!merged.siteName?.trim()) { notify.warning("Site Name is required."); return; }
    try {
      await api.put(`/api/v1/clients/${encodeURIComponent(clientId)}/sample-sites/${r.oid}`, merged);
      notify.success("Saved successfully."); void load();
    } catch (e) { notify.error(serverMessage(e) ?? "Failed to save the sample site."); }
  }, [clientId, notify, load, pwsNames, pwsIds, waterTypes, collectors]);

  return (
    <>
      <TabGrid columns={[
        /* GoLive Customer_SampleSites_ListView — full 42-column order. FK columns
           show the resolved display name (read-only); the plain text columns the
           UPDATE persists keep inline editKey. */
        { header: 'SiteID', render: r => r.siteID ?? '' },
        { header: 'SiteName', render: r => r.siteName, editKey: 'siteName' },
        { header: 'SiteCode', render: r => r.siteCode ?? '', editKey: 'siteCode' },
        { header: 'KeyMap', render: r => r.keyMap ?? '', editKey: 'keyMap' },
        { header: 'SamplePointID', render: r => r.samplePointID ?? '', editKey: 'samplePointID' },
        { header: 'Blended', render: r => r.blended ? 'Yes' : 'No', editKey: 'blended', editType: 'check' },
        { header: 'ServiceArea', render: r => r.serviceArea ?? '', editKey: 'serviceArea' },
        { header: 'Description', render: r => r.description ?? '', editKey: 'description' },
        { header: 'PWSName', render: r => r.pwsName ?? '', editKey: 'pwsName', editType: 'combo', options: () => pwsNames.map(o => o.name) },
        { header: 'ProjectID', render: r => r.projectIdCode ?? '' },
        { header: 'SystemType', render: r => r.systemType ?? '' },
        { header: 'ProjectName', render: r => r.projectName ?? '', editKey: 'projectName' },
        { header: 'PWSID', render: r => r.pwsIdCode ?? '', editKey: 'pwsIdCode', editType: 'combo', options: () => pwsIds.map(o => o.name) },
        { header: 'CollectorUserID', render: r => r.siteUserDefinedColumn1 ?? '', editKey: 'siteUserDefinedColumn1' },
        { header: 'IsActive', render: r => r.isActive ? 'Yes' : 'No', editKey: 'isActive', editType: 'check' },
        { header: 'SiteNameArchived', render: r => r.siteNameArchived ?? '', editKey: 'siteNameArchived' },
        { header: 'MonitoringRequirement', render: r => r.monitoryingRequirement ?? '', editKey: 'monitoryingRequirement' },
        { header: 'FacilityID', render: r => r.facilityID ?? '', editKey: 'facilityID' },
        { header: 'FacilityName', render: r => r.facilityName ?? '', editKey: 'facilityName' },
        { header: 'FacilityType', render: r => r.facilityType ?? '', editKey: 'facilityType' },
        { header: 'SamplePointType', render: r => r.samplePointType ?? '', editKey: 'samplePointType' },
        { header: 'WaterType', render: r => r.waterTypeName ?? '', editKey: 'waterTypeName', editType: 'combo', options: () => waterTypes.map(o => o.name) },
        { header: 'City', render: r => r.city ?? '', editKey: 'city' },
        { header: 'State', render: r => r.state ?? '', editKey: 'state' },
        { header: 'ZipCode', render: r => r.zipCode ?? '', editKey: 'zipCode' },
        { header: 'SampleType', render: r => r.sampleType ?? '' },
        { header: 'SamplingAddress', render: r => r.samplingAddress ?? '' },
        { header: 'StationLocation', render: r => r.stationLocation ?? '' },
        { header: 'Depth', render: r => r.depth ?? '' },
        { header: 'SampleID', render: r => r.sampleID ?? '' },
        { header: 'SampleNo', render: r => r.sampleNo ?? '' },
        { header: 'Latitude', render: r => r.latitude ?? '' },
        { header: 'Longitude', render: r => r.longitude ?? '' },
        { header: 'SampleMatrix', render: r => r.sampleMatrix ?? '' },
        { header: 'Matrix', render: r => r.matrix ?? '' },
        { header: 'Qty', render: r => r.qty == null ? '' : String(r.qty) },
        { header: 'Collector', render: r => r.collectorName ?? '', editKey: 'collectorName', editType: 'combo', options: () => collectors.map(o => o.name) },
        { header: 'CollectorPhone', render: r => r.collectorPhone ?? '' },
        { header: 'ParentSampleID', render: r => r.parentSampleID ?? '' },
        { header: 'ParentSampleDate', render: r => r.parentSampleDate ?? '' },
        { header: 'RepeatLocation', render: r => r.repeatLocation ?? '' },
        { header: 'RejectionCriteria', render: r => r.rejectionCriteria ?? '' },
      ]} rows={rows} loading={loading} exportName="SampleSites" onNew={() => setEditing('new')} onEdit={setEditing} onDelete={r => void remove(r)}
         onInlineSave={inlineSave} />
      {editing && (
        <div style={miniModal} onClick={() => setEditing(null)}>
          <SiteEditForm clientId={clientId} row={editing === 'new' ? null : editing}
                        onClose={() => setEditing(null)} onSaved={() => { setEditing(null); void load(); }} notify={notify} />
        </div>
      )}
    </>
  );
}

/**
 * Full GoLive "Sample Sites" edit form (Customer_SampleSites_DetailView). Loads
 * the FIVE FK comboboxes from real data exactly like the DevExpress lookups:
 *   • Client   → dbo.Customer         (/sample-receipt-notification/clients)
 *   • ProjectID→ dbo.Project          (/clients/lookups/all-projects)
 *   • PWSID    → dbo.PWSSystemID       (/clients/lookups/pws-ids)   + ✎ New popup
 *   • PWSName  → dbo.PWSSystem         (/clients/lookups/pws-names) + ✎ New popup
 *   • WaterType→ dbo.WaterTypes        (/clients/lookups/water-types)
 * On Edit the FULL record (GET .../sample-sites/{oid}) seeds every field + FK id.
 * SiteID is auto-generated server-side ("SS#####") when left blank on New.
 */
function SiteEditForm({ clientId, row, onClose, onSaved, notify }: {
  clientId: string; row: SiteRow | null; onClose: () => void; onSaved: () => void; notify: NotifyApi;
}) {
  const cid = encodeURIComponent(clientId);
  const [loading, setLoading] = useState(!!row);
  const [saving, setSaving] = useState(false);

  // ── FK lookup lists ───────────────────────────────────────────────────
  const [clients, setClients]       = useState<Opt[]>([]);
  const [projects, setProjects]     = useState<Opt[]>([]);
  const [pwsIds, setPwsIds]         = useState<Opt[]>([]);
  const [pwsNames, setPwsNames]     = useState<Opt[]>([]);
  const [waterTypes, setWaterTypes] = useState<Opt[]>([]);
  const [collectors, setCollectors] = useState<Opt[]>([]);
  const [pwsIdModal, setPwsIdModal]   = useState(false);
  const [pwsNameModal, setPwsNameModal] = useState(false);

  // ── Field state ───────────────────────────────────────────────────────
  const [siteName, setSiteName] = useState(row?.siteName ?? '');
  // Site ID is server-auto-generated ("SS#####") and shown read-only ("Autogenerated"),
  // matching GoLive; SamplePointID/Blended are grid-only fields GoLive's form omits —
  // all three keep their existing value on edit (no setter, so no accidental wipe).
  const [siteId]                = useState(row?.siteID ?? '');
  const [address, setAddress]   = useState(row?.address ?? '');
  const [city, setCity]         = useState(row?.city ?? '');
  const [state, setState]       = useState(row?.state ?? '');
  const [zip, setZip]           = useState(row?.zipCode ?? '');
  const [isActive, setIsActive] = useState(row?.isActive ?? true);
  const [siteCode, setSiteCode] = useState(row?.siteCode ?? '');
  const [keyMap, setKeyMap]     = useState(row?.keyMap ?? '');
  const [samplePointId]         = useState(row?.samplePointID ?? '');
  const [blended]               = useState(row?.blended ?? false);
  const [serviceArea, setServiceArea] = useState(row?.serviceArea ?? '');
  const [description, setDescription] = useState(row?.description ?? '');
  const [udc1, setUdc1]           = useState(row?.siteUserDefinedColumn1 ?? '');
  const [siteNameArchived, setSiteNameArchived] = useState(row?.siteNameArchived ?? '');
  const [monitoring, setMonitoring] = useState(row?.monitoryingRequirement ?? '');
  const [facilityId, setFacilityId] = useState(row?.facilityID ?? '');
  const [facilityName, setFacilityName] = useState(row?.facilityName ?? '');
  const [facilityType, setFacilityType] = useState(row?.facilityType ?? '');
  const [samplePointType, setSamplePointType] = useState(row?.samplePointType ?? '');
  const [projectName, setProjectName] = useState(row?.projectName ?? '');
  // FK ids (seeded from the full record on Edit; Client defaults to the parent).
  const [clientFk, setClientFk]     = useState<string | null>(clientId);
  const [projectFk, setProjectFk]   = useState<string | null>(row?.projectId ?? null);
  const [pwsIdFk, setPwsIdFk]       = useState<string | null>(null);
  const [pwsNameFk, setPwsNameFk]   = useState<string | null>(null);
  const [waterTypeFk, setWaterTypeFk] = useState<string | null>(row?.waterTypeId ?? null);
  const [collectorFk, setCollectorFk] = useState<string | null>(null);

  // Load the FK lists once (real data — same lists the DevExpress combos bind to).
  useEffect(() => {
    void api.get('/api/v1/sample-receipt-notification/clients')
      .then(r => { const rows = (r.data as { data?: { oid: string; name: string }[] }).data ?? [];
                   setClients(rows.map(c => ({ id: c.oid, name: c.name }))); }).catch(() => {});
    void api.get('/api/v1/clients/lookups/all-projects').then(r => setProjects((r.data as { data?: Opt[] }).data ?? [])).catch(() => {});
    void api.get('/api/v1/clients/lookups/pws-ids').then(r => setPwsIds((r.data as { data?: Opt[] }).data ?? [])).catch(() => {});
    void api.get('/api/v1/clients/lookups/pws-names').then(r => setPwsNames((r.data as { data?: Opt[] }).data ?? [])).catch(() => {});
    void api.get('/api/v1/clients/lookups/water-types').then(r => setWaterTypes((r.data as { data?: Opt[] }).data ?? [])).catch(() => {});
    void api.get(`/api/v1/clients/${cid}/collector-options`).then(r => setCollectors((r.data as { data?: Opt[] }).data ?? [])).catch(() => {});
  }, [cid]);

  // On Edit, seed EVERY field from the full record (the grid row is a subset and
  // has no PWSID / PWSName / Client ids).
  useEffect(() => {
    if (!row) return;
    let cancelled = false;
    setLoading(true);
    void api.get(`/api/v1/clients/${cid}/sample-sites/${row.oid}`)
      .then(r => {
        if (cancelled) return;
        const d = (r.data as { data?: SiteDetail }).data;
        if (d) {
          setClientFk(d.clientId ?? clientId); setProjectFk(d.projectId ?? null);
          setPwsIdFk(d.pwsId ?? null); setPwsNameFk(d.pwsNameId ?? null);
          setWaterTypeFk(d.waterTypeId ?? null); setCollectorFk(d.collector ?? null);
          setProjectName(d.projectName ?? '');
        }
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [row, cid, clientId]);

  const save = useCallback(async () => {
    if (!siteName.trim()) { notify.warning('Site Name is required.'); return; }
    setSaving(true);
    try {
      const body = {
        siteName, siteID: siteId, address, description, city, state, zipCode: zip, isActive,
        siteCode, keyMap, samplePointID: samplePointId, blended, serviceArea,
        projectId: projectFk, projectName, siteUserDefinedColumn1: udc1, siteNameArchived,
        monitoryingRequirement: monitoring, facilityID: facilityId, facilityName, facilityType,
        samplePointType, waterTypeId: waterTypeFk, pwsId: pwsIdFk, pwsNameId: pwsNameFk,
        collector: collectorFk,
      };
      // The site always belongs to the client whose detail we're inside; if the
      // user re-picked Client, POST/PUT under that client instead.
      const owner = encodeURIComponent(clientFk || clientId);
      if (row) await api.put(`/api/v1/clients/${owner}/sample-sites/${row.oid}`, body);
      else await api.post(`/api/v1/clients/${owner}/sample-sites`, body);
      notify.success('Saved successfully.'); onSaved();
    } catch (e) { notify.error(serverMessage(e) ?? 'Failed to save site.'); } finally { setSaving(false); }
  }, [siteName, siteId, address, description, city, state, zip, isActive, siteCode, keyMap,
      samplePointId, blended, serviceArea, projectFk, projectName, udc1, siteNameArchived, monitoring,
      facilityId, facilityName, facilityType, samplePointType, waterTypeFk, pwsIdFk, pwsNameFk,
      collectorFk, clientFk, row, clientId, notify, onSaved]);

  // GoLive lays the Sample Sites detail out in TWO columns; each field is a
  // label(130px):control row. Helpers keep the JSX flat + consistent.
  const row2: CSSProperties = { display: 'grid', gridTemplateColumns: '132px 1fr', alignItems: 'center', gap: 8, marginBottom: 9 };
  const txt = (label: string, v: string, on: (s: string) => void, required = false): ReactNode => (
    <div style={row2}><label style={{ fontSize: 11 }}>{label}:{required && <span style={{ color: '#d32f2f' }}>*</span>}</label>
      <input style={fInput(required && !v.trim())} value={v} onChange={e => on(e.target.value)} /></div>
  );
  // Read-only field (GoLive greys Site ID — it is auto-generated on save).
  const ro = (label: string, v: string, placeholder?: string): ReactNode => (
    <div style={row2}><label style={{ fontSize: 11 }}>{label}</label>
      <input style={{ ...fInput(false), background: '#f3f4f6', color: '#6b7280' }} value={v} placeholder={placeholder} readOnly /></div>
  );
  const chk = (label: string, v: boolean, on: (b: boolean) => void): ReactNode => (
    <div style={row2}><label style={{ fontSize: 11 }}>{label}</label>
      <input type="checkbox" checked={v} onChange={e => on(e.target.checked)} /></div>
  );
  // FK combobox row: capped scrollable Combo (real data) + optional ✎ New + eraser.
  const fk = (label: string, value: string | null, opts: Opt[], on: (v: string | null) => void, onNew?: () => void): ReactNode => (
    <div style={row2}><label style={{ fontSize: 11 }}>{label}</label>
      <AiCombo value={value} options={opts.map(o => ({ value: o.id, label: o.name }))}
               onChange={on} onNew={onNew} newTitle="New / Edit" erasable /></div>
  );

  return (
    <>
      <MiniShell width={860} title={row ? 'Sample Sites' : 'Sample Sites'} onClose={onClose}
                 footer={<MiniOkCancel onOk={() => void save()} onCancel={onClose} saving={saving || loading} />}>
        {/* GoLive Customer_SampleSites_DetailView — exact two-column field order. */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 32px', padding: '16px 18px', maxHeight: '72vh', overflowY: 'auto' }}>
          <div>
            {ro('Site ID', siteId, 'Autogenerated')}
            {fk('Client', clientFk, clients, setClientFk)}
            {txt('Address', address, setAddress)}
            {txt('Site Code', siteCode, setSiteCode)}
            {fk('PWSID', pwsIdFk, pwsIds, setPwsIdFk, () => setPwsIdModal(true))}
            {fk('PWSName', pwsNameFk, pwsNames, setPwsNameFk, () => setPwsNameModal(true))}
            {txt('Key Map', keyMap, setKeyMap)}
            {txt('Service Area', serviceArea, setServiceArea)}
            {txt('Description', description, setDescription)}
            {fk('Project ID', projectFk, projects, setProjectFk)}
            {txt('Project Name', projectName, setProjectName)}
            {txt('CollectorUserID', udc1, setUdc1)}
            {fk('Collector', collectorFk, collectors, setCollectorFk)}
          </div>
          <div>
            {chk('Is Active', isActive, setIsActive)}
            {txt('Site Name Archived', siteNameArchived, setSiteNameArchived)}
            {txt('Site Name', siteName, setSiteName, true)}
            {txt('Monitoring Requirement', monitoring, setMonitoring)}
            {txt('Facility ID', facilityId, setFacilityId)}
            {txt('Facility Name', facilityName, setFacilityName)}
            {txt('Facility Type', facilityType, setFacilityType)}
            {txt('Sample Point Type', samplePointType, setSamplePointType)}
            {fk('Water Type', waterTypeFk, waterTypes, setWaterTypeFk)}
            {txt('City', city, setCity)}
            {txt('State', state, setState)}
            {txt('Zip Code', zip, setZip)}
          </div>
        </div>
      </MiniShell>
      {pwsIdModal && (
        <div style={miniModal} onClick={() => setPwsIdModal(false)}>
          <PwsCreateModal kind="id" onClose={() => setPwsIdModal(false)} notify={notify}
            onCreated={async (oid) => {
              try { const r = await api.get('/api/v1/clients/lookups/pws-ids'); setPwsIds((r.data as { data?: Opt[] }).data ?? []); } catch { /* keep */ }
              setPwsIdFk(oid); setPwsIdModal(false);
            }} />
        </div>
      )}
      {pwsNameModal && (
        <div style={miniModal} onClick={() => setPwsNameModal(false)}>
          <PwsCreateModal kind="name" onClose={() => setPwsNameModal(false)} notify={notify}
            onCreated={async (oid) => {
              try { const r = await api.get('/api/v1/clients/lookups/pws-names'); setPwsNames((r.data as { data?: Opt[] }).data ?? []); } catch { /* keep */ }
              setPwsNameFk(oid); setPwsNameModal(false);
            }} />
        </div>
      )}
    </>
  );
}

/** The "PWS System ID" / "PWS System Name" mini create popup (Sample Site ✎). */
function PwsCreateModal({ kind, onClose, onCreated, notify }: {
  kind: 'id' | 'name'; onClose: () => void; onCreated: (oid: string) => void; notify: NotifyApi;
}) {
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);
  const label = kind === 'id' ? 'PWS System ID' : 'PWS System Name';
  // G5 captions the box PWSID: / PWSName: (the popup title keeps the long name).
  const caption = kind === 'id' ? 'PWSID' : 'PWSName';
  const path = kind === 'id' ? 'pws-ids' : 'pws-names';
  const save = useCallback(async () => {
    if (!value.trim()) { notify.warning(`${label} is required.`); return; }
    setSaving(true);
    try {
      const r = await api.post(`/api/v1/clients/lookups/${path}`, { value });
      const oid = (r.data as { data?: { oid?: string } }).data?.oid;
      notify.success('Saved successfully.'); onCreated(oid ?? '');
    } catch { notify.error(`Failed to save the ${label}.`); } finally { setSaving(false); }
  }, [value, label, path, notify, onCreated]);
  return (
    <MiniShell title={label} onClose={onClose}
               footer={<MiniOkCancel onOk={() => void save()} onCancel={onClose} saving={saving} />}>
      <div style={{ padding: '12px 0' }}>
        <div style={miniRow}><label style={{ fontSize: 11 }}>{caption}:<span style={{ color: '#d32f2f' }}>*</span></label>
          <input style={fInput(!value.trim())} value={value} onChange={e => setValue(e.target.value)} autoFocus /></div>
      </div>
    </MiniShell>
  );
}

// ── Notes tab (author-only edit/delete — §14a Rule C) ──────────────────
interface NoteRow { oid: string; title: string | null; plainText: string | null; createdBy: string | null; createdDate: string | null; canEdit: boolean;
  attachment: string | null; isCaseNarrative: boolean; followUpDate: string | null; sort: number | null; }
/** The CRM Notes popup config with the clear button on its Contact and Category lookups (G5's Maintenance popup shows it).
 *  Built here, not in moduleCrud.ts, because that config is shared with CRM and must not change there. */
function maintNoteConfig() {
  const base = MODULE_CRUD['crm-customer-note']!;
  return { ...base, fields: base.fields.map((f) => (f.key === 'contactId' || f.key === 'categoryId' ? { ...f, erasable: true } : f)) };
}

/** The Notes tab's Link popup (G5 nested-list Link): notes not on any client; ticking and OK files them under this one. */
function NotesLinkDialog({ clientId, onClose, onLinked, notify }: {
  clientId: string; onClose: () => void; onLinked: () => void; notify: NotifyApi;
}) {
  interface LinkableNote { oid: string; title: string | null; note: string | null; noteDate: string | null; author: string | null }
  const [rows, setRows] = useState<LinkableNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Set<string | number>>(new Set());
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get(`/api/v1/clients/${encodeURIComponent(clientId)}/notes/linkable`)
      .then(r => setRows((r.data as { data?: LinkableNote[] }).data ?? []))
      .catch(() => notify.error('Failed to load notes.')).finally(() => setLoading(false));
  }, [clientId, notify]);
  const columns: AppColumn<LinkableNote>[] = [
    { field: 'title', header: 'Title', width: 220 },
    { field: 'note', header: 'Note', width: 320, value: r => (r.note ?? '').replace(/<[^>]*>/g, ' ').slice(0, 80) },
    { field: 'author', header: 'Author', width: 130 },
    { field: 'noteDate', header: 'Date', width: 130, type: 'datetime' },
  ];
  const link = async () => {
    if (selected.size === 0) return;
    setSaving(true);
    try {
      for (const oid of selected) await api.post(`/api/v1/clients/${encodeURIComponent(clientId)}/notes/${oid}/link`);
      notify.success('Linked.'); onLinked();
    } catch (e) { notify.error(serverMessage(e) ?? 'Failed to link.'); } finally { setSaving(false); }
  };
  return (
    <AppDialog open title="Notes" icon="ti-link" width={860} onClose={onClose}
      footer={(
        <>
          <button className="ca-btn ca-btn-solid" disabled={selected.size === 0 || saving} onClick={() => void link()}>OK</button>
          <button className="ca-btn" onClick={onClose}>Cancel</button>
        </>
      )}>
      <AppDataGrid<LinkableNote>
        rows={rows} columns={columns} getRowId={r => r.oid} loading={loading}
        selectable selectedIds={selected} onSelectionChange={setSelected}
        defaultPageSize={20} emptyText="No data to display" hideHeaderTools />
    </AppDialog>
  );
}

function NotesTab({ clientId, clientName, notify, confirm }: { clientId: string; clientName: string; notify: NotifyApi; confirm: ConfirmFn }) {
  const [rows, setRows] = useState<NoteRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<NoteRow | 'new' | null>(null);
  const [linkOpen, setLinkOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await api.get(`/api/v1/clients/${encodeURIComponent(clientId)}/notes`); setRows((res.data as { data: NoteRow[] }).data ?? []); }
    catch { notify.error('Failed to load notes.'); } finally { setLoading(false); }
  }, [clientId, notify]);
  useEffect(() => { void load(); }, [load]);

  const remove = useCallback(async (r: NoteRow) => {
    if (!r.canEdit) { notify.error('You are not authorized to modify a note created by another author.'); return; }
    const ok = await confirm({ title: 'Delete', message: `Delete note "${r.title}"?`, tone: 'danger' });
    if (!ok) return;
    try { await api.delete(`/api/v1/clients/${encodeURIComponent(clientId)}/notes/${r.oid}`); notify.success('Deleted successfully.'); void load(); }
    catch (e) { notify.error(serverMessage(e) ?? 'Failed to delete note.'); }
  }, [clientId, confirm, notify, load]);

  // G5 nested-list Unlink: the note stays, it just leaves this client.
  const unlink = useCallback(async (picked: NoteRow[]) => {
    const ok = await confirm({ title: 'Unlink', tone: 'danger',
      message: picked.length === 1 ? `Unlink "${picked[0]?.title ?? ''}" from this client?` : `Unlink ${picked.length} notes from this client?` });
    if (!ok) return;
    try {
      for (const r of picked) await api.post(`/api/v1/clients/${encodeURIComponent(clientId)}/notes/${r.oid}/unlink`);
      notify.success('Unlinked.');
    } catch (e) { notify.error(serverMessage(e) ?? 'Failed to unlink.'); }
    void load();
  }, [clientId, confirm, notify, load]);

  const editOrWarn = useCallback((r: NoteRow) => {
    if (!r.canEdit) { notify.error('You are not authorized to modify a note created by another author.'); return; }
    setEditing(r);
  }, [notify]);

  return (
    <>
      <TabGrid columns={[
        // G5's Notes nested list: Title · Attachment · Note · Author · Date · IsCaseNarrative · FollowUpDate · sort.
        { header: 'Title', render: r => r.title ?? '' },
        { header: 'Attachment', render: r => r.attachment ?? '' },
        { header: 'Note', render: r => (r.plainText ?? '').slice(0, 80) },
        { header: 'Author', render: r => r.createdBy ?? '' },
        { header: 'Date', render: r => (r.createdDate ? fmtLocalDateTime(r.createdDate) : '') },
        { header: 'IsCaseNarrative', render: r => <input type="checkbox" checked={r.isCaseNarrative} readOnly onClick={e => e.stopPropagation()} style={{ display: 'block', margin: '0 auto' }} /> },
        { header: 'FollowUpDate', render: r => (r.followUpDate ? fmtLocalDate(r.followUpDate) : '') },
        { header: 'sort', render: r => (r.sort ?? '') },
      ]} rows={rows} loading={loading} exportName="Notes" onNew={() => setEditing('new')} onEdit={editOrWarn} onDelete={r => void remove(r)}
      onLink={() => setLinkOpen(true)} onUnlink={r => void unlink(r)} />
      {linkOpen && (
        <NotesLinkDialog clientId={clientId} onClose={() => setLinkOpen(false)}
                         onLinked={() => { setLinkOpen(false); void load(); }} notify={notify} />
      )}
      {/* G5's Notes popup: the SAME form the CRM Notes in Clients page uses (Title · Client · Contact · Phone · Email · Note /
          Follow Up Date · Date · Author · Category · Email To · Attachment) over the shared /api/v1/crm-notes backend. */}
      {editing && (
        <ModuleCrudDialog config={maintNoteConfig()}
                          id={editing === 'new' ? null : editing.oid} mode={editing === 'new' ? undefined : 'edit'}
                          contextValues={{ ownerId: clientId, client: clientName }}
                          onClose={() => setEditing(null)} onSaved={() => { setEditing(null); void load(); }} />
      )}
    </>
  );
}

